/**
 * Backend trusts API — typed TanStack Query hooks.
 *
 * Wraps the per-trust read+write surface:
 *   - `useTrusts()` / `useTrust(id)` — list + detail.
 *   - `useTrustAuditLog({ trustId })` — `GET /v1/app/trusts/{id}/audit`.
 *   - `useTrustDocuments({ trustId })` — `GET /v1/app/trusts/{id}/documents`.
 *   - `useTrustMeetings({ trustId })` — `GET /v1/trusts/{id}/meetings`.
 *   - `useTrustRiskRating(trustId)` — `GET /v1/app/trusts/{id}/risk-rating`.
 *   - `useCreateTrustMeeting()` — `POST /v1/app/trusts/{id}/meetings`.
 *   - `useUpdateTrustRiskRating()` — `POST /v1/app/trusts/{id}/risk-rating`.
 *
 * Phase 3 Task 3.4b additions (2026-05-17):
 *   - `useTrustBeneficiaries({ trustId, includeOwnership? })` — `GET /v1/trusts/{id}/beneficiaries`.
 *   - `useUpdateBeneficiary()` — `PATCH /v1/trusts/{id}/beneficiaries/{benId}`.
 *   - `useTrustOwnership(trustId)` — `GET /v1/trusts/{id}/ownership` (TPCA s1 tree).
 *   - `useTrustTaxCertificates({ trustId, year? })` — `GET /v1/trusts/{id}/tax/certificates`.
 *   - `useCreateTaxCertificate()` — `POST /v1/trusts/{id}/tax/certificates`.
 *   - `useTrustLoaLow(trustId)` — `GET /v1/trusts/{id}/loa-low` (combined panel).
 *   - `useUpdateLoa()` — `POST /v1/trusts/{id}/loa`.
 *   - `useUpdateLow()` — `POST /v1/trusts/{id}/low`.
 *   - `useTrustResolutions({ trustId, status?, limit?, cursor? })` — `GET /v1/resolutions?trustId=`.
 *
 * Response shapes mirror the backend wire format
 * (`backend/src/handlers/trusts-*-{list,get,update,create}.ts`).
 *
 * Phase 3 Task 3.4a — wires the per-trust drill-down screens at
 * `/app/trusts/$id/{audit,documents,meetings,risk-rating}` to the real
 * backend endpoints that shipped in Phase 2 Task 2.4a (commit b829f3b).
 * Phase 3 Task 3.4b — wires the remaining 5 drill-down screens at
 * `/app/trusts/$id/{beneficiaries,beneficiaries/ownership,tax,loa-low,resolutions}`
 * to the backend endpoints shipped in Phase 2 Task 2.4b (commit cf4b2c8).
 * Replaces the hardcoded "van der Merwe Family Trust" / "Naledi Mokoena" /
 * "IT 8842/2008" fixture data flagged in the 2026-05-16 audit (P0/P1 leak).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api, ApiError } from './client';
import type {
  ResolutionStatus,
  ResolutionSummary,
} from './resolutions';
import type { TrustReviewItem } from './manifest-types';
import type { MastersOffice } from '../reference/masters-offices';

// ── Trust list + detail ────────────────────────────────────────────────────

/** The readable surfaces of a trust. Mirrors `TrustReadSurface` in
 *  `backend/src/lib/trust-read-surface.ts` — the backend decides membership of
 *  this set per caller per trust; the client only reads the answer. */
export type TrustReadSurface =
  | 'identity'
  | 'documents'
  | 'governance'
  | 'financials'
  | 'distributions-all'
  | 'distributions-own'
  | 'people'
  | 'compliance'
  | 'compliance-own'
  | 'operations';

/**
 * May the caller read this surface of this trust?
 *
 * ⚠️ THIS IS A RENDERING DECISION, NOT A SECURITY BOUNDARY. Every surface is
 * enforced server-side by the handler that serves it; this exists so a
 * beneficiary sees a coherent, smaller app instead of a wall of 403s. Hiding a
 * panel the backend would refuse is the whole job.
 *
 * FAILS OPEN when `readableSurfaces` is absent, and that is deliberate. Absence
 * means an older backend that has not been taught to send the field — and in
 * that world the old handler is also still serving the data, so hiding the panel
 * would remove something the user can legitimately see. The backend is the gate;
 * if it starts refusing, it will also have started sending this field.
 */
export function canReadSurface(
  trust: Pick<TrustSummary, 'readableSurfaces'> | undefined | null,
  surface: TrustReadSurface,
): boolean {
  const surfaces = trust?.readableSurfaces;
  if (!surfaces) return true; // no answer from the API → render as before
  return surfaces.includes(surface);
}

export interface TrustSummary {
  id: string;
  tenantId: string;
  name: string;
  registration: string;
  /** ISO date of trust-deed signing, or null when not yet known (a name-only /
   *  governance-only trust has no deed date — rendered as "—"). */
  deedDate: string | null;
  /** ISO date the Master registered the trust (sourced from the Letter of
   *  Authority — distinct from the deed-signing date, e.g. Beno's deed
   *  20 Mar 2000 vs LoA registration 29 Mar 2000). Omitted by the API when
   *  no LoA date is known. */
  registrationDate?: string | null;
  /** WHICH Master of the High Court office the trust is registered at, one of
   *  the sixteen strings the Master's own register workbook accepts
   *  (`lib/reference/masters-offices.ts`). Recorded by an administrator on the
   *  trust's settings screen; lodged verbatim in column 4 of the
   *  beneficial-ownership register export.
   *
   *  OMITTED by the API when nobody has recorded one. Absent means the export
   *  leaves that column for the filer, never that an office was chosen. */
  mastersOffice?: MastersOffice;
  /** What the DEED says about how beneficiaries receive (Beno, 2026-08-01).
   *  Absent, or `basis: 'not-determined'`, means the deed did not say — NOT
   *  that it is discretionary by default. A determined basis always carries the
   *  verbatim clause behind it. Rendered by `<TrustKeyFacts>`. */
  deedDistribution?: {
    basis: 'equal' | 'fixed-shares' | 'discretionary' | 'not-determined';
    quote: string | null;
    clauseReference: string | null;
  } | null;
  /** The HONEST registered/verified total (whole Rand). Never includes
   *  claimed-pending value — see `claimedPendingValue`.
   *
   *  ⚠️ OPTIONAL BECAUSE OF THE READ TIER, not because of an old backend. The
   *  API OMITS this field for a caller who may not read the `financials` surface
   *  of this trust — a beneficiary. Absence means "you are not shown this", NOT
   *  "R0": rendering a zero would state that a trust holding twelve million rand
   *  holds nothing. Every read site must branch on `=== undefined` and render
   *  nothing (or a dash), never `?? 0`.
   *
   *  Use `canReadSurface(trust, 'financials')` to decide whether to render the
   *  surrounding block at all, rather than testing this field directly — the
   *  whole point is to hide the panel, not to show an empty one. */
  totalValue?: number;
  /** Wave-6 row 4: the SECOND figure — the sum of THIS trust's
   *  `earmarked_for_trust` asset values (whole Rand), shown alongside
   *  `totalValue`, never added into it. `0` when the trust has no pending
   *  assets. Absent from an old backend → treat as 0. */
  claimedPendingValue?: number;
  /** What `totalValue` reflects (Wave 1 / B4): `'holdings'` = live holdings
   *  sum; `'afs'` = the AFS net-asset-value stands (no valued holdings yet).
   *  Absent from an old backend — render no chip. */
  totalValueBasis?: 'holdings' | 'afs';
  /** `financials` surface — omitted with `totalValue`. Absent ≠ 0% (a flat year
   *  and a withheld figure are different statements). */
  yoyPct?: number;
  /** `financials` surface — omitted with `totalValue`. */
  yoyAmount?: number;
  /** AFS-GRADING findings 1 + 2 — the confirmed annual financial statements
   *  record for THIS trust, in WHOLE RAND exactly as the statements present
   *  them. `financials` surface — OMITTED (the key itself) for a caller who
   *  may not read it, same as `totalValue`/`yoyPct`/`yoyAmount` above. When
   *  present: `null` when no AFS has ever been confirmed for this trust, or
   *  the record when one has. Mirrors `DashboardSummary.afsFinancials` in
   *  `./dashboard.ts` — same shape, same honesty rule (no derived figures,
   *  no fabricated period). */
  afsFinancials?: {
    periodEndDate: string | null;
    netAssetValue?: number;
    totalAssets?: number;
    totalLiabilities?: number;
    totalIncome?: number;
    totalExpenses?: number;
    distributionsToBeneficiaries?: number;
  } | null;
  lastUpdated: string;
  /** TRS-03 — the review clock, and it is ABSENT (both keys) on a trust with no
   *  initial FICA on file. The backend withholds it at the seam
   *  (`backend/src/lib/fica-review-clock.ts`) precisely so a screen that forgets
   *  the guard renders nothing rather than a countdown on a file nobody has
   *  verified once. Read it through `hasFicaReviewClock` / `ficaDaysOutOf` in
   *  `src/lib/fica-review.ts`; never `?? 0`, which would print "overdue". */
  nextFicaReview?: string;
  ficaDaysOut?: number;
  /** TRS-03: whether anyone on the register is FICA-verified (`false` = no
   *  initial FICA, so the clock above is absent; `null` = unknown, which keeps
   *  it). Absent from an older backend. */
  initialFicaOnFile?: boolean | null;
  /** `people` surface — OMITTED for a caller who may not read it. Absent ≠ an
   *  empty roster: a beneficiary reading this is themselves a member, so `[]`
   *  would be visibly false. Never `?? []` this into a member count. */
  memberIds?: string[];
  createdAt: string;
  updatedAt: string;
  /** The caller's tier on THIS trust. Present on list rows and on the detail
   *  response; `null` when they see it by role (firm staff) rather than by
   *  membership. */
  callerTier?: 'trustee' | 'beneficiary' | null;
  /** Which surfaces of THIS trust the caller may read, decided by the single
   *  backend policy (`backend/src/lib/trust-read-surface.ts`) and shipped so the
   *  app hides what it cannot show. Absent from an older backend — see
   *  `canReadSurface` for the fail-open reasoning. */
  readableSurfaces?: TrustReadSurface[];
  /** dossier-depth-20260711: PII-clean "documents disagreed on X" items from
   *  the reconcile commit that founded/last touched this trust — feeds the
   *  trust-overview "Action required" card. Absent when the commit surfaced
   *  no field conflicts. Never carries the divergent values themselves. */
  reviewItems?: TrustReviewItem[];
}

export interface TrustsListResponse {
  trusts: TrustSummary[];
  /** Opaque cursor for the next page (#2). Null/absent on the legacy
   *  full-list response (no pagination params). */
  nextCursor?: string | null;
  correlationId: string;
}

export interface TrustGetResponse {
  trust: TrustSummary;
  /** The caller's tier on THIS trust ('trustee' | 'beneficiary'), or null when
   *  they see it only via tenant-wide read access (admin / firm reader) and are
   *  not themselves a member. Lets the portal tier-gate write UI — a
   *  beneficiary-tier member gets a read-only "People & access" tab. Mirrors the
   *  backend `TrustGetResponse.callerTier` (`trusts-get.ts`). */
  callerTier?: 'trustee' | 'beneficiary' | null;
  correlationId: string;
}

/** Query key + fetcher shared by the hook AND the auth-time dashboard
 *  prefetch (`prefetch-dashboard.ts`) — one definition, so the prefetched
 *  cache entry is guaranteed to be the exact one the dashboard reads. */
export const TRUSTS_LIST_QUERY_KEY = ['trusts', 'list'] as const;

export function fetchTrustsList(signal?: AbortSignal): Promise<TrustsListResponse> {
  return api<TrustsListResponse>('/api/v1/trusts', { signal });
}

export function useTrusts(opts: { enabled?: boolean } = {}) {
  return useQuery<TrustsListResponse>({
    queryKey: TRUSTS_LIST_QUERY_KEY,
    queryFn: ({ signal }) => fetchTrustsList(signal),
    enabled: opts.enabled ?? true,
  });
}

export function useTrust(trustId: string | undefined) {
  return useQuery<TrustGetResponse>({
    queryKey: ['trusts', 'detail', trustId ?? null],
    queryFn: ({ signal }) =>
      api<TrustGetResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId!)}`,
        { signal },
      ),
    enabled: !!trustId,
  });
}

/** True when the current user may take write actions on this trust:
 *  a trustee-tier member, OR an admin/firm operator acting by role
 *  (callerTier null). A beneficiary-tier member is read-only. */
export function useCanActOnTrust(trustId: string | undefined): boolean {
  const { data } = useTrust(trustId);
  const tier = data?.callerTier;
  return tier == null || tier === 'trustee';
}

/**
 * The surfaces of this trust the current user may read — the hook the whole app
 * hides panels by.
 *
 * Returns a PREDICATE rather than the raw list so no call site has to remember
 * how absence behaves (see `canReadSurface`: it fails open, on purpose), and so
 * the loading window has one answer instead of one per screen.
 *
 * ⚠️ `isPending` matters. While the trust detail is in flight there is no answer
 * yet, and the predicate answers `true` — the pre-tier behaviour. Rendering a
 * panel that then disappears is the lesser evil versus flashing "you cannot see
 * this" at a trustee on every cold load, which is the shape of the 2026-08-12
 * compliance-card bug (cards claiming all-clear for 5.3 s while loading). Call
 * sites that show a REFUSAL rather than just hiding content should gate on
 * `isPending` themselves.
 */
export function useTrustSurfaces(trustId: string | undefined): {
  can: (surface: TrustReadSurface) => boolean;
  tier: 'trustee' | 'beneficiary' | null | undefined;
  isPending: boolean;
} {
  const { data, isPending } = useTrust(trustId);
  const trust = data?.trust;
  return {
    can: (surface: TrustReadSurface) => canReadSurface(trust, surface),
    tier: data?.callerTier,
    isPending,
  };
}

// ── Create a trust (self-serve, Task 6.6) ──────────────────────────────────
//
// Wraps `POST /v1/trusts` (see `backend/src/handlers/trusts-create.ts`). A
// self-serve trustee/settlor who administers more than one trust can add a
// second (or third) trust to their own book without going through a firm
// admin. The creator becomes the first member (`memberIds: [caller]`), the
// trust lands in draft state (totalValue 0, FICA review +30 days), and no
// step-up is required — it's still a draft on creation.
//
// On success we invalidate the `['trusts']` query family so the trust-list /
// picker picks up the new row immediately.

export interface CreateTrustRequest {
  /** Trust name as registered with the Master. */
  name: string;
  /** Master registration / IT number. */
  registration: string;
  /** ISO date of the trust-deed signing (YYYY-MM-DD). */
  deedDate: string;
}

export interface CreateTrustResponse {
  trust: TrustSummary;
  correlationId: string;
}

export function useCreateTrust() {
  const qc = useQueryClient();
  return useMutation<CreateTrustResponse, ApiError, CreateTrustRequest>({
    mutationFn: (body) =>
      api<CreateTrustResponse>('/api/v1/trusts', { method: 'POST', body }),
    onSuccess: () => {
      // The new trust must appear immediately in the trust list + picker
      // (GET /v1/trusts). Invalidate the whole `trusts` family so the list,
      // any detail prefetches, and the cross-trust picker all refresh.
      void qc.invalidateQueries({ queryKey: ['trusts'] });
    },
  });
}

export interface UpdateTrustRequest {
  name?: string;
  registration?: string;
  /** Next FICA review date (ISO). */
  nextFicaReview?: string;
  /** One of the Master's sixteen offices, or `''` to clear the field back to
   *  "not recorded". The backend validates against the same closed list and
   *  refuses anything else; `''` is the one clear this route allows, because a
   *  wrongly-recorded office is a wrong fact lodged with a regulator. */
  mastersOffice?: MastersOffice | '';
  /** Trustee decision rule (deed-driven; advisory quorum). */
  trusteeDecisionRule?: {
    basis: 'majority' | 'unanimous' | 'super-majority';
    superMajorityPct?: number;
    quorumMin?: number;
  };
}

/**
 * PATCH /v1/trusts/{trustId} — edit a trust's deed fields (backend handler
 * `trusts-patch.ts`, already shipped). name/registration/decision-rule are
 * admin-only server-side; nextFicaReview is member-allowed. Used by the firm's
 * inline "Edit details" drawer UNDER an act-as token — the plain api() call rides
 * the active firm-act token, so the write lands in the CLIENT's own tenant with
 * no signature change. Invalidates the trust detail + list.
 */
export function useUpdateTrust(trustId: string) {
  const qc = useQueryClient();
  return useMutation<{ trust: TrustSummary; correlationId: string }, ApiError, UpdateTrustRequest>({
    mutationFn: (patch) =>
      api<{ trust: TrustSummary; correlationId: string }>(
        `/api/v1/trusts/${encodeURIComponent(trustId)}`,
        { method: 'PATCH', body: patch },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['trusts', 'detail', trustId] });
      void qc.invalidateQueries({ queryKey: ['trusts', 'list'] });
    },
  });
}

// ── Per-trust audit log ────────────────────────────────────────────────────
//
// Wraps `GET /v1/app/trusts/{trustId}/audit` (see
// `backend/src/handlers/trusts-audit-log-list.ts`). Returns the
// tenant-scoped audit_log rows filtered to events touching this trust —
// full forensic shape (subjectIdHash, performedByRole, prevHash/hash).

export interface TrustAuditEntryView {
  id: string;
  tenantId: string;
  action: string;
  subjectType: string;
  subjectId: string;
  subjectIdHash?: string;
  subjectIdHashKeyId?: string;
  performedByRole?: string;
  correlationId?: string;
  metadata?: Record<string, unknown>;
  prevHash?: string;
  hash?: string;
  ts: string;
  createdAt: string;
}

export interface TrustAuditLogResponse {
  audit: TrustAuditEntryView[];
  nextCursor: string | null;
  /** COMBO-013: true when this trust has more audit events than the
   *  single-page tenant-wide over-fetch window could surface — the returned
   *  `audit` is a partial view. A CSV export should warn the user rather than
   *  present a silently-truncated audit trail as complete. Optional for
   *  back-compat with responses minted before the flag existed. */
  truncated?: boolean;
  correlationId: string;
}

export interface UseTrustAuditLogOptions {
  trustId: string | undefined;
  limit?: number;
  cursor?: string;
  /** Event-name prefix filter, e.g. `"resolution."`, `"bo."`, `"fic."`. */
  event?: string;
  enabled?: boolean;
}

function buildAuditPath(opts: UseTrustAuditLogOptions): string {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.cursor) params.set('cursor', opts.cursor);
  if (opts.event) params.set('event', opts.event);
  const qs = params.toString();
  return (
    `/api/v1/app/trusts/${encodeURIComponent(opts.trustId!)}/audit` +
    (qs ? `?${qs}` : '')
  );
}

export function useTrustAuditLog(opts: UseTrustAuditLogOptions) {
  return useQuery<TrustAuditLogResponse>({
    queryKey: [
      'trusts',
      'audit',
      opts.trustId ?? null,
      opts.limit ?? null,
      opts.cursor ?? null,
      opts.event ?? null,
    ],
    queryFn: ({ signal }) =>
      api<TrustAuditLogResponse>(buildAuditPath(opts), { signal }),
    enabled: (opts.enabled ?? true) && !!opts.trustId,
  });
}

// ── Per-trust documents ────────────────────────────────────────────────────
//
// Wraps `GET /v1/app/trusts/{trustId}/documents` (see
// `backend/src/handlers/trusts-documents-list.ts`). Documents are
// metadata-only — `blobPath` is intentionally omitted from list rows
// (SAS URL minting happens via a separate endpoint).

export type TrustDocumentStatus =
  | 'pending-scan'
  | 'verified'
  | 'rejected-malware'
  | 'rejected-other';

/** P2-2 per-document redundancy classification (within the trust's documents):
 *  'current' (newest of its type), 'superseded' (older, a newer one exists), or
 *  'duplicate' (an identical re-upload). Drives the vault status badge. */
export type DocumentRedundancy = 'current' | 'superseded' | 'duplicate';

export interface TrustDocumentView {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  status: TrustDocumentStatus | string;
  eSignAllowed: boolean;
  documentType: string;
  trustId?: string;
  uploadedByUserId: string;
  /** The uploader's account display name (or their email), resolved
   *  server-side; null when the account cannot be named, and ABSENT from a
   *  backend older than round 11. Prefer it over any client-side roster: under
   *  an act-as posture the browser has no name for the caller at all
   *  (`GET /v1/users/me` answers with an empty displayName). */
  uploadedByDisplayName?: string | null;
  uploadedAt: string;
  scannedAt?: string;
  /** P2-2 redundancy status (always present). */
  redundancy: DocumentRedundancy;
  /** SUPERSEDE-SPEC §3.4: for a `superseded` row, the id of the CURRENT (newest)
   *  document in the same instrument lineage — resolved to a filename for the
   *  "superseded by <doc>" pointer. Absent on current / duplicate rows. */
  supersededById?: string;
  /** SUPERSEDE-SPEC §3.5: true when this row is an identity-bearing document with
   *  no derived instrument key and a same-class sibling is present — the vault
   *  asks "same instrument as X?". Absent/false otherwise. */
  needsReview?: boolean;
  /** P2-2 archive metadata — present only on archived rows (the "show archived"
   *  view). Absent = an active document. */
  archivedAt?: string;
  archivedReason?: string;
}

export interface TrustDocumentsResponse {
  documents: TrustDocumentView[];
  /** P2-2: required core docs the trust is missing (the per-trust "valuable but
   *  missing" hint). Reflects ALL on-file docs, not the filtered slice. */
  missingCore: string[];
  nextCursor: string | null;
  correlationId: string;
}

export interface UseTrustDocumentsOptions {
  trustId: string | undefined;
  limit?: number;
  cursor?: string;
  /** Filter by documentType, e.g. `"trust-deed" | "resolution" | "minutes"`. */
  type?: string;
  /** P2-2: include soft-archived documents (the vault's "show archived" toggle).
   *  Default false → archived rows are hidden. */
  includeArchived?: boolean;
  enabled?: boolean;
}

function buildDocumentsPath(opts: UseTrustDocumentsOptions): string {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.cursor) params.set('cursor', opts.cursor);
  if (opts.type) params.set('type', opts.type);
  if (opts.includeArchived) params.set('includeArchived', 'true');
  const qs = params.toString();
  return (
    `/api/v1/app/trusts/${encodeURIComponent(opts.trustId!)}/documents` +
    (qs ? `?${qs}` : '')
  );
}

export function useTrustDocuments(opts: UseTrustDocumentsOptions) {
  return useQuery<TrustDocumentsResponse>({
    queryKey: [
      'trusts',
      'documents',
      opts.trustId ?? null,
      opts.limit ?? null,
      opts.cursor ?? null,
      opts.type ?? null,
      opts.includeArchived ?? false,
    ],
    queryFn: ({ signal }) =>
      api<TrustDocumentsResponse>(buildDocumentsPath(opts), { signal }),
    enabled: (opts.enabled ?? true) && !!opts.trustId,
  });
}

// ── Per-trust trustee meetings ─────────────────────────────────────────────
//
// Wraps:
//   - `GET  /v1/trusts/{trustId}/meetings`           — list summaries.
//   - `POST /v1/app/trusts/{trustId}/meetings`       — schedule a draft.
// (Note the GET/POST path asymmetry — preserved from the backend handler
// configuration; see `trusts-meetings-list.ts` and
// `trusts-meetings-create.ts`. The POST sits under `/v1/app/` because
// the create endpoint went through the verified-idempotency middleware
// pass during Phase 2 Task 2.4a.)

export type TrustMeetingStatus = 'scheduled' | 'in-progress' | 'finalised';

export interface TrustMeetingView {
  id: string;
  title: string;
  date: string;
  status: TrustMeetingStatus;
  chairUserId: string;
  attendees: number;
  agendaCount: number;
  resolutionsTabled: number;
  /** Agenda items + the decision recorded against each — the meeting record.
   *  Optional on the type (older mocks omit it); the backend always sends it. */
  agendaItems?: { title: string; discussion: string; decision?: string }[];
  /** Attendee user ids — the detail modal resolves these to names + derives apologies. */
  attendeeUserIds?: string[];
  trustId: string;
  /** WS-4 register backfill: `'imported-document'` marks a meeting auto-created
   *  from an ingested minutes document rather than scheduled in-app. Drives the
   *  "Imported" badge on the meetings register. */
  source?: 'app' | 'imported-document';
  importedFromDocumentId?: string;
}

export interface TrustMeetingsResponse {
  meetings: TrustMeetingView[];
  correlationId: string;
}

export interface UseTrustMeetingsOptions {
  trustId: string | undefined;
  limit?: number;
  /** ISO date lower bound (inclusive) on meetingDate — now a server-side
   *  filter (#15). */
  from?: string;
  /** ISO date upper bound (inclusive) on meetingDate — server-side (#15). */
  to?: string;
  /** Restrict to meetings a given user attended / chaired / minuted (id match,
   *  server-side; #15). */
  attendee?: string;
  /** Free-text search over the meeting TITLE (server-side substring; #15). */
  q?: string;
  enabled?: boolean;
}

export function useTrustMeetings(opts: UseTrustMeetingsOptions) {
  return useQuery<TrustMeetingsResponse>({
    queryKey: [
      'trusts',
      'meetings',
      opts.trustId ?? null,
      opts.limit ?? null,
      opts.from ?? null,
      opts.to ?? null,
      opts.attendee ?? null,
      opts.q ?? null,
    ],
    queryFn: async ({ signal }) => {
      // #15: from/to/attendee/q are now real server-side params on the GET
      // handler, so the previous client-side date filtering is gone.
      const params = new URLSearchParams();
      if (opts.from) params.set('from', opts.from);
      if (opts.to) params.set('to', opts.to);
      if (opts.attendee) params.set('attendee', opts.attendee);
      if (opts.q) params.set('q', opts.q);
      const qs = params.toString();
      const res = await api<TrustMeetingsResponse>(
        `/api/v1/trusts/${encodeURIComponent(opts.trustId!)}/meetings` +
          (qs ? `?${qs}` : ''),
        { signal },
      );
      // `limit` stays a client-side cap — the meetings GET has no cursor today
      // (low row counts per trust), so trimming the returned page is honest.
      const meetings =
        opts.limit !== undefined ? res.meetings.slice(0, opts.limit) : res.meetings;
      return { ...res, meetings };
    },
    enabled: (opts.enabled ?? true) && !!opts.trustId,
  });
}

export type TrustMeetingType =
  | 'in-person'
  | 'remote'
  | 'circulating-resolution'
  | 'agm';

export interface CreateTrustMeetingAgendaItem {
  title: string;
  discussion: string;
}

export interface CreateTrustMeetingRequest {
  trustId: string;
  title: string;
  meetingType: TrustMeetingType;
  meetingDate: string;
  agendaItems: CreateTrustMeetingAgendaItem[];
  chairUserId?: string;
  minuteKeeperUserId?: string;
}

export interface CreateTrustMeetingResponse {
  meeting: TrustMeetingView & {
    meetingType: TrustMeetingType;
    agendaItems: CreateTrustMeetingAgendaItem[];
    createdAt: string;
    updatedAt: string;
  };
  correlationId: string;
}

export function useCreateTrustMeeting() {
  const qc = useQueryClient();
  return useMutation<
    CreateTrustMeetingResponse,
    ApiError,
    CreateTrustMeetingRequest
  >({
    mutationFn: ({ trustId, ...body }) =>
      api<CreateTrustMeetingResponse>(
        `/api/v1/app/trusts/${encodeURIComponent(trustId)}/meetings`,
        { method: 'POST', body },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['trusts', 'meetings', vars.trustId],
      });
    },
  });
}

// ── Per-trust risk rating ──────────────────────────────────────────────────
//
// Wraps:
//   - `GET  /v1/app/trusts/{trustId}/risk-rating` — read singleton (or null).
//   - `POST /v1/app/trusts/{trustId}/risk-rating` — full replacement upsert.
// See `backend/src/handlers/trusts-risk-rating-{get,update}.ts`.

export type TrustRiskTier = 'low' | 'medium' | 'high' | 'critical';

export type TrustRiskFactorCategory =
  | 'geography'
  | 'products'
  | 'channels'
  | 'customer-type'
  | 'other';

export interface TrustRiskFactor {
  category: TrustRiskFactorCategory;
  description: string;
  tier: TrustRiskTier;
}

export interface TrustRiskRatingView {
  id: string;
  tenantId: string;
  trustId: string;
  tier: TrustRiskTier;
  score: 1 | 2 | 3 | 4 | 5;
  factors: TrustRiskFactor[];
  reviewedByUserId: string;
  reviewedAt: string;
  rationale?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TrustRiskRatingGetResponse {
  rating: TrustRiskRatingView | null;
  correlationId: string;
}

/**
 * AC4 read/write routing for the FIRM console (QUE-01, 2026-08-27).
 *
 * A trust in the risk-review queue usually lives in a CLIENT's tenant, not the
 * firm's, so a compliance officer scoring it from `/admin/risk-review` has to
 * execute against that client's tenant on the firm's grant. The established
 * seam is a per-client act-as token minted by `useFirmClientAct` and carried on
 * the api() `actAsToken` option for THAT CALL ONLY — the console's global
 * posture is untouched, which is what keeps a firm from silently ending up
 * "inside" a client it never asked to enter. Same shape `useOpenEddCase` uses.
 *
 * Grant-gated server-side: a `manage` grant mints an 'admin' token and the
 * write runs; a `read` grant mints a write-denied 'firm-reader' token and the
 * write 403s honestly. Never a silent wrong-tenant write.
 */
export interface ActAsRouted {
  /** Per-client act-as token. Omit for a trust in the caller's own tenant. */
  actAsToken?: string | undefined;
}

export function useTrustRiskRating(
  trustId: string | undefined,
  opts: ActAsRouted & { enabled?: boolean } = {},
) {
  return useQuery<TrustRiskRatingGetResponse>({
    // The trust id is unique across tenants, so it alone keys this read; the
    // token decides WHICH session may see it, never which document it is.
    queryKey: ['trusts', 'risk-rating', trustId ?? null],
    queryFn: ({ signal }) =>
      api<TrustRiskRatingGetResponse>(
        `/api/v1/app/trusts/${encodeURIComponent(trustId!)}/risk-rating`,
        { signal, ...(opts.actAsToken ? { actAsToken: opts.actAsToken } : {}) },
      ),
    enabled: !!trustId && opts.enabled !== false,
  });
}

export interface UpdateTrustRiskRatingRequest {
  trustId: string;
  tier: TrustRiskTier;
  score: 1 | 2 | 3 | 4 | 5;
  factors: TrustRiskFactor[];
  rationale?: string;
}

export interface UpdateTrustRiskRatingResponse {
  rating: TrustRiskRatingView;
  correlationId: string;
}

export function useUpdateTrustRiskRating() {
  const qc = useQueryClient();
  return useMutation<
    UpdateTrustRiskRatingResponse,
    ApiError,
    UpdateTrustRiskRatingRequest & ActAsRouted
  >({
    // `actAsToken` rides the AUTH HEADER, never the request body — it is a
    // session, not a field, and the handler must never see it as data.
    mutationFn: ({ trustId, actAsToken, ...body }) =>
      api<UpdateTrustRiskRatingResponse>(
        `/api/v1/app/trusts/${encodeURIComponent(trustId)}/risk-rating`,
        { method: 'POST', body, ...(actAsToken ? { actAsToken } : {}) },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['trusts', 'risk-rating', vars.trustId],
      });
      // The changelog is derived from the audit chain — a new rating adds a
      // history entry, so refetch the history view too.
      void qc.invalidateQueries({
        queryKey: ['trusts', 'risk-rating', 'history', vars.trustId],
      });
    },
  });
}

// ── Per-trust risk-rating changelog (feature backlog #14) ──────────────────
//
// Wraps `GET /v1/app/trusts/{trustId}/risk-rating/history`
// (see `backend/src/handlers/trusts-risk-rating-history.ts`). The current
// rating is a singleton doc; prior ratings live only in the audit chain.
// This view projects the `risk-rating.updated` audit rows into a clean,
// newest-first changelog. Each entry is derived strictly from what the audit
// row carries — there is intentionally no free-text `reason` (the rationale
// lives only on the current rating doc, not in the audit payload).

export interface TrustRiskRatingHistoryEntry {
  /** The rating's id (== trustId). Null on malformed legacy rows. */
  ratingId: string | null;
  rating: TrustRiskTier | null;
  score: number | null;
  previousRating: TrustRiskTier | null;
  previousScore: number | null;
  firstReview: boolean;
  /** Effective actor who set the rating (a Cosmos user id — not a name). */
  changedBy: string | null;
  /** ISO timestamp of the change. */
  changedAt: string;
  correlationId: string | null;
}

export interface TrustRiskRatingHistoryResponse {
  history: TrustRiskRatingHistoryEntry[];
  truncated: boolean;
  correlationId: string;
}

export function useTrustRiskRatingHistory(trustId: string | undefined) {
  return useQuery<TrustRiskRatingHistoryResponse>({
    queryKey: ['trusts', 'risk-rating', 'history', trustId ?? null],
    queryFn: ({ signal }) =>
      api<TrustRiskRatingHistoryResponse>(
        `/api/v1/app/trusts/${encodeURIComponent(trustId!)}/risk-rating/history`,
        { signal },
      ),
    enabled: !!trustId,
  });
}

// ── Per-trust beneficiaries (Task 3.4b) ────────────────────────────────────
//
// Wraps `GET /v1/trusts/{trustId}/beneficiaries[?includeOwnership=true]`
// (see `backend/src/handlers/trusts-beneficiaries-list.ts`). The trust-deed
// beneficiary register; distinct from the FIC TPCA s1 ownership tree below.
// PII fields like `idNumber`/`taxNumber` are AE-decrypted server-side and
// only present for PII-visible roles — mask via `maskPii()` at the UI layer.

export interface BeneficiaryRow {
  id: string;
  tenantId: string;
  trustId: string;
  kind: 'named' | 'class';
  displayName: string;
  initials: string;
  avColor: string;
  relationship: string;
  badge?: string;
  ytdDistributed?: number;
  userId?: string;
  displayOrder: number;
  status: 'active' | 'archived';
  /** Is this beneficiary still alive? (meeting ruling, 2026-08-22.)
   *
   *  ABSENT MEANS ALIVE — no row written before that date carries the field and
   *  nothing backfills them. Orthogonal to `status`: a deceased beneficiary is
   *  still `status: 'active'` and still comes back in the list, because the
   *  register has to keep its history ("beneficiary … until 13 Jan 2013")
   *  rather than losing the row the day someone dies. */
  lifeStatus?: 'active' | 'deceased';
  /** ISO-8601 calendar date (`YYYY-MM-DD`) of death. Only ever present together
   *  with `lifeStatus: 'deceased'` — the backend writes and clears the pair
   *  atomically. Not scrubbed for non-PII roles: it is the register's own
   *  history, and the trustees who read this surface are the ones who need it. */
  deceasedOn?: string;
  /** AE-decrypted ID number — only present for PII-visible roles. */
  idNumber?: string;
  /** AE-decrypted tax number — only present for PII-visible roles. */
  taxNumber?: string;
  /** AE-decrypted passport — only present for PII-visible roles. */
  passportNumber?: string;
  createdAt: string;
  updatedAt: string;
}

/** Projected onto the beneficiaries response when
 *  `includeOwnership=true` — TPCA s1 ownership summary inline so the
 *  drill-down screen can render with one round-trip. */
export interface BoOwnerSummary {
  id: string;
  ownerType: 'natural-person' | 'juristic-person';
  fullName: string;
  category: 'settlor' | 'trustee' | 'beneficiary' | 'protector' | 'other';
  tpcaSubClause: 'a' | 'b' | 'c' | 'd' | 'e';
  ficaStatus?: 'pending' | 'verified' | 'rejected';
  /** 'not_screened' = added but no sanctions/PEP screening ran (no provider) —
   *  NOT a clean verdict. */
  screeningStatus?: 'clean' | 'flagged' | 'not_screened' | 'pending';
  screeningRiskTier?: 'low' | 'medium' | 'high' | 'critical';
  nationality?: string;
  countryOfTaxResidence?: string;
  idNumber?: string;
  passportNumber?: string;
  dateOfBirth?: string;
}

export interface TrustBeneficiariesResponse {
  beneficiaries: BeneficiaryRow[];
  beneficialOwners?: BoOwnerSummary[];
  correlationId: string;
}

export interface UseTrustBeneficiariesOptions {
  trustId: string | undefined;
  includeOwnership?: boolean;
  enabled?: boolean;
}

export function useTrustBeneficiaries(opts: UseTrustBeneficiariesOptions) {
  return useQuery<TrustBeneficiariesResponse>({
    queryKey: [
      'trusts',
      'beneficiaries',
      opts.trustId ?? null,
      { includeOwnership: !!opts.includeOwnership },
    ],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (opts.includeOwnership) params.set('includeOwnership', 'true');
      const qs = params.toString();
      return api<TrustBeneficiariesResponse>(
        `/api/v1/trusts/${encodeURIComponent(opts.trustId!)}/beneficiaries` +
          (qs ? `?${qs}` : ''),
        { signal },
      );
    },
    enabled: (opts.enabled ?? true) && !!opts.trustId,
  });
}

export interface UpdateBeneficiaryRequest {
  trustId: string;
  beneficiaryId: string;
  /** Partial patch — mirrors backend `PatchBeneficiarySchema`. */
  patch: Partial<{
    kind: 'named' | 'class';
    displayName: string;
    initials: string;
    avColor: string;
    relationship: string;
    badge: string;
    ytdDistributed: number;
    userId: string;
    displayOrder: number;
    status: 'active' | 'archived';
    /** Beneficiary lifecycle (2026-08-22 ruling). The two fields are a PAIR
     *  server-side and the backend 400s (`LIFECYCLE_PAIR_REQUIRED`) on any
     *  combination that would leave a contradictory row:
     *    - record a death → `{ lifeStatus: 'deceased', deceasedOn: 'YYYY-MM-DD' }`
     *    - undo one recorded in error → `{ lifeStatus: 'active' }` ALONE, which
     *      clears the stored date rather than leaving it stale.
     *  A future `deceasedOn` is refused (`DECEASED_ON_FUTURE`). */
    lifeStatus: 'active' | 'deceased';
    deceasedOn: string;
  }>;
}

export interface UpdateBeneficiaryResponse {
  beneficiary: BeneficiaryRow;
  correlationId: string;
}

export function useUpdateBeneficiary() {
  const qc = useQueryClient();
  return useMutation<
    UpdateBeneficiaryResponse,
    ApiError,
    UpdateBeneficiaryRequest
  >({
    mutationFn: ({ trustId, beneficiaryId, patch }) =>
      api<UpdateBeneficiaryResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId)}/beneficiaries/${encodeURIComponent(beneficiaryId)}`,
        { method: 'PATCH', body: patch },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['trusts', 'beneficiaries', vars.trustId],
      });
    },
  });
}

// ── Per-trust ownership tree (TPCA s1) (Task 3.4b) ─────────────────────────
//
// Wraps `GET /v1/trusts/{trustId}/ownership` (see
// `backend/src/handlers/trusts-ownership-list.ts`). FIC TPCA s1 look-through
// ownership tree grouped by category. PII fields (idNumber, passportNumber,
// dateOfBirth) are scrubbed for non-PII-visible roles.

export interface OwnershipNode {
  id: string;
  ownerType: 'natural-person' | 'juristic-person';
  fullName: string;
  category: 'settlor' | 'trustee' | 'beneficiary' | 'protector' | 'other';
  tpcaSubClause: 'a' | 'b' | 'c' | 'd' | 'e';
  ficaStatus?: 'pending' | 'verified' | 'rejected';
  /** FICA-screening rejection reason — set when ficaStatus === 'rejected'
   *  (COMBO-020). Screening metadata, not subject PII; surfaced to all
   *  members alongside the status badge. */
  ficaRejectReason?: string | null;
  /** 'not_screened' = added but no sanctions/PEP screening ran (no provider) —
   *  NOT a clean verdict; blocks Master lodgement until screening completes. */
  screeningStatus?: 'clean' | 'flagged' | 'not_screened' | 'pending';
  screeningRiskTier?: 'low' | 'medium' | 'high' | 'critical';
  nationality?: string;
  countryOfTaxResidence?: string;
  idNumber?: string;
  passportNumber?: string;
  dateOfBirth?: string;
  // ── Reg 3C(1) particulars (TPCA reg 3C(1)) ─────────────────────────────────
  /** Citizenship (ISO-3166-1 alpha-2), distinct from nationality. All viewers. */
  citizenship?: string;
  /** ISO date the person became a beneficial owner. All viewers. */
  becameBoOn?: string;
  /** ISO date the person ceased to be a beneficial owner (if applicable). */
  ceasedBoOn?: string;
  /** Vault document id of the certified ID copy (reg 3C(3)). All viewers. */
  certifiedIdDocumentId?: string;
  /** Reg 3C(1) elevated PII — privileged (admin/compliance) readers only. */
  taxNumber?: string;
  residentialAddress?: string;
  serviceAddress?: string;
  email?: string;
  phone?: string;
  guardianFullName?: string;
  guardianRelationship?: string;
  guardianContact?: string;
  /** True when no MANDATORY reg 3C(1) particular is missing for lodgement.
   *  Optional so older fixtures compile; the backend always returns it. */
  regComplete?: boolean;
  /** Machine-readable keys of the missing mandatory reg 3C(1) particulars. */
  regMissingFields?: string[];
}

export interface OwnershipTree {
  trustId: string;
  settlors: OwnershipNode[];
  trustees: OwnershipNode[];
  beneficiaries: OwnershipNode[];
  protectors: OwnershipNode[];
  other: OwnershipNode[];
  totalCount: number;
  lodgementPending: number;
}

export interface TrustOwnershipResponse {
  ownership: OwnershipTree;
  correlationId: string;
}

export function useTrustOwnership(trustId: string | undefined) {
  return useQuery<TrustOwnershipResponse>({
    queryKey: ['trusts', 'ownership', trustId ?? null],
    queryFn: ({ signal }) =>
      api<TrustOwnershipResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId!)}/ownership`,
        { signal },
      ),
    enabled: !!trustId,
  });
}

// ── Per-trust tax certificates (Task 3.4b) ─────────────────────────────────
//
// Wraps `GET /v1/trusts/{trustId}/tax/certificates[?year=YYYY]` and
// `POST /v1/trusts/{trustId}/tax/certificates` (see
// `backend/src/handlers/trusts-tax-certificates-*.ts`).
// Certificates: IT3(t) / IT3(b) per beneficiary per tax year.

export type TaxCertificateType = 'IT3(t)' | 'IT3(b)';
export type TaxCertificateStatus =
  | 'draft'
  | 'issued'
  | 'amended'
  | 'superseded';

export interface TaxCertificate {
  id: string;
  tenantId: string;
  trustId: string;
  beneficiaryId: string;
  beneficiaryName: string;
  taxYear: number;
  certificateType: TaxCertificateType;
  /** Whole ZAR (NOT cents — backend stores int rands). */
  amount: number;
  status: TaxCertificateStatus;
  documentId?: string;
  sarsReference?: string;
  issuedAt?: string;
  supersededBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TrustTaxCertificatesResponse {
  certificates: TaxCertificate[];
  /** WHICH question the backend answered (2026-08-18).
   *
   *  `'trust'` — the trust's whole IT3(t) register (a trustee, or firm staff).
   *  `'own'`   — only the certificates issued to the CALLER, joined via their
   *              beneficiary record. A beneficiary reads their own SARS
   *              paperwork because it is addressed to them; the register is not
   *              theirs.
   *
   *  The screen must title itself from this. Showing an own-scoped list under
   *  "Tax certificates issued by this trust to its beneficiaries" would imply
   *  the trust issued exactly one, to them. Absent from an older backend →
   *  treat as `'trust'`, which is what it used to be. */
  scope?: 'trust' | 'own';
  correlationId: string;
}

export interface UseTrustTaxCertificatesOptions {
  trustId: string | undefined;
  /** Optional SARS tax year filter (e.g. 2025 covers 1 Mar 2024 → 28 Feb 2025). */
  year?: number;
  enabled?: boolean;
}

export function useTrustTaxCertificates(opts: UseTrustTaxCertificatesOptions) {
  return useQuery<TrustTaxCertificatesResponse>({
    queryKey: [
      'trusts',
      'tax',
      'certificates',
      opts.trustId ?? null,
      { year: opts.year ?? null },
    ],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (opts.year !== undefined) params.set('year', String(opts.year));
      const qs = params.toString();
      return api<TrustTaxCertificatesResponse>(
        `/api/v1/trusts/${encodeURIComponent(opts.trustId!)}/tax/certificates` +
          (qs ? `?${qs}` : ''),
        { signal },
      );
    },
    enabled: (opts.enabled ?? true) && !!opts.trustId,
  });
}

export interface CreateTaxCertificateRequest {
  trustId: string;
  beneficiaryId: string;
  taxYear: number;
  certificateType: TaxCertificateType;
  /** Whole ZAR; backend validates int, non-negative. */
  amount: number;
}

export interface CreateTaxCertificateResponse {
  certificate: TaxCertificate;
  correlationId: string;
}

export function useCreateTaxCertificate() {
  const qc = useQueryClient();
  return useMutation<
    CreateTaxCertificateResponse,
    ApiError,
    CreateTaxCertificateRequest
  >({
    mutationFn: ({ trustId, ...body }) =>
      api<CreateTaxCertificateResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId)}/tax/certificates`,
        { method: 'POST', body },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['trusts', 'tax', 'certificates', vars.trustId],
      });
    },
  });
}

// ── Per-trust LoA + LoW combined panel (Task 3.4b) ─────────────────────────
//
// Wraps:
//   - `GET  /v1/trusts/{trustId}/loa-low` — combined Letter of Authority +
//     Letter of Wishes for the drill-down screen.
//   - `POST /v1/trusts/{trustId}/loa`     — supersede current LoA with new.
//   - `POST /v1/trusts/{trustId}/low`     — append a new LoW version.
// See `backend/src/handlers/trusts-loa-low-get.ts` and the loa-update /
// low-update siblings.

export interface LoaDoc {
  id: string;
  tenantId: string;
  trustId: string;
  status: 'current' | 'superseded';
  /** The ordering key. Do NOT display it without checking
   *  `effectiveFromAssumed` — see below. */
  effectiveFrom: string;
  /** True when ingestion could not read the Master's issue date off the
   *  document and substituted the ingestion date to keep the history
   *  orderable. Display "Not recorded", never the substituted date: this is a
   *  legal instrument and the date is not on it (2026-08-25). */
  effectiveFromAssumed?: boolean;
  supersededAt?: string;
  trustees: string[];
  reason: string;
  masterReference?: string;
  documentId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface LowDoc {
  id: string;
  tenantId: string;
  trustId: string;
  version: number;
  effectiveFrom: string;
  bodySummary: string;
  documentId?: string;
  settlorUserId?: string;
  status: 'current' | 'superseded';
  createdAt: string;
  updatedAt: string;
  /** Only present on the `low.latest` row. */
  acknowledgedByMe?: boolean;
  acknowledgedAt?: string;
  /** WS-4 register backfill: `'imported-document'` marks a version auto-created
   *  from an ingested document rather than authored in-app. Drives the
   *  "Imported" badge on the Letters of Wishes register. */
  source?: 'app' | 'imported-document';
  importedFromDocumentId?: string;
}

export interface TrustLoaLowResponse {
  loa: {
    current: LoaDoc | null;
    history: LoaDoc[];
    count: number;
  };
  low: {
    latest: LowDoc | null;
    versions: LowDoc[];
    count: number;
  };
  correlationId: string;
}

export function useTrustLoaLow(trustId: string | undefined) {
  return useQuery<TrustLoaLowResponse>({
    queryKey: ['trusts', 'loa-low', trustId ?? null],
    queryFn: ({ signal }) =>
      api<TrustLoaLowResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId!)}/loa-low`,
        { signal },
      ),
    enabled: !!trustId,
  });
}

export interface UpdateLoaRequest {
  trustId: string;
  effectiveFrom: string;
  trustees: string[];
  reason: string;
  masterReference?: string;
  documentId?: string;
}

export interface UpdateLoaResponse {
  loa: LoaDoc;
  correlationId: string;
}

export function useUpdateLoa() {
  const qc = useQueryClient();
  return useMutation<UpdateLoaResponse, ApiError, UpdateLoaRequest>({
    mutationFn: ({ trustId, ...body }) =>
      api<UpdateLoaResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId)}/loa`,
        { method: 'POST', body },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['trusts', 'loa-low', vars.trustId],
      });
    },
  });
}

export interface UpdateLowRequest {
  trustId: string;
  effectiveFrom: string;
  bodySummary: string;
  documentId?: string;
  settlorUserId?: string;
}

export interface UpdateLowResponse {
  low: LowDoc;
  correlationId: string;
}

export function useUpdateLow() {
  const qc = useQueryClient();
  return useMutation<UpdateLowResponse, ApiError, UpdateLowRequest>({
    mutationFn: ({ trustId, ...body }) =>
      api<UpdateLowResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId)}/low`,
        { method: 'POST', body },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['trusts', 'loa-low', vars.trustId],
      });
    },
  });
}

// ── Letter of Wishes acknowledge (Fix #4b) ─────────────────────────────────
//
// Wraps `POST /v1/letters-of-wishes/{letterId}/acknowledgements`
// (`backend/src/handlers/letters-of-wishes-acknowledge.ts`). The backend was
// complete and `trusts-loa-low-get.ts` already returned `acknowledgedByMe` /
// `acknowledgedAt` on `low.latest` — but no frontend caller existed. The
// backend requires `userId` in the body to equal the caller's JWT `sub` (no
// admin override), so the caller must supply the current user's id (via
// `useMe()`). NOT step-up gated (routine read-back-confirmation, not a
// money-move) — no `isStepUpRequired` special-case needed by callers.

export interface AcknowledgeLowRequest {
  letterId: string;
  userId: string;
  attestation?: string;
  /** The letter's trust — used purely to invalidate the loa-low panel. Not sent. */
  trustId: string;
}

export interface AcknowledgeLowResponse {
  acknowledgement: { letterId: string; userId: string; acknowledgedAt: string; attestation?: string };
  replayed: boolean;
  correlationId: string;
}

export function useAcknowledgeLow() {
  const qc = useQueryClient();
  return useMutation<AcknowledgeLowResponse, ApiError, AcknowledgeLowRequest>({
    mutationFn: ({ letterId, trustId: _trustId, ...body }) =>
      api<AcknowledgeLowResponse>(
        `/api/v1/letters-of-wishes/${encodeURIComponent(letterId)}/acknowledgements`,
        { method: 'POST', body },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['trusts', 'loa-low', vars.trustId],
      });
    },
  });
}

// ── Per-trust resolutions (Task 3.4b) ──────────────────────────────────────
//
// Wraps `GET /v1/resolutions?trustId=&status=&limit=` (see
// `backend/src/handlers/resolutions-list.ts`). The existing list endpoint
// already satisfies REQ-TRUSTS-008 — backend Task 2.4b documented +
// locked the contract.
//
// Re-uses `ResolutionStatus` / `ResolutionSummary` from `./resolutions` so
// the per-trust hook stays type-compatible with the cross-trust list and the
// resolution-detail screen. `cursor` is forward-compat — the current backend
// handler returns a flat list keyed off the limit; `nextCursor` is reserved
// on the response wrapper.

export interface TrustResolutionsResponse {
  resolutions: ResolutionSummary[];
  /** Forward-compat cursor — always undefined today. */
  nextCursor?: string | null;
  correlationId: string;
}

export interface UseTrustResolutionsOptions {
  trustId: string | undefined;
  status?: ResolutionStatus;
  limit?: number;
  cursor?: string;
  enabled?: boolean;
}

export function useTrustResolutions(opts: UseTrustResolutionsOptions) {
  return useQuery<TrustResolutionsResponse>({
    queryKey: [
      'trusts',
      'resolutions',
      opts.trustId ?? null,
      {
        status: opts.status ?? null,
        limit: opts.limit ?? null,
        cursor: opts.cursor ?? null,
      },
    ],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ trustId: opts.trustId! });
      if (opts.status) params.set('status', opts.status);
      if (opts.limit !== undefined) params.set('limit', String(opts.limit));
      if (opts.cursor) params.set('cursor', opts.cursor);
      return api<TrustResolutionsResponse>(
        `/api/v1/resolutions?${params.toString()}`,
        { signal },
      );
    },
    enabled: (opts.enabled ?? true) && !!opts.trustId,
  });
}

// ── Per-trust trustees roster ──────────────────────────────────────────────
//
// H-84 (2026-05-20 — Henry persona walk): exposes the full trustee roster
// so resolution-creation can default `signerUserIds` to ALL trustees on
// the trust (rather than the single caller — which Henry surfaced as
// "0 / 1 signatures expected on a 3-trustee trust").
//
// Wraps `GET /v1/trusts/{trustId}/trustees` (see
// `backend/src/handlers/trusts-trustees-list.ts`). Rows without a `userId`
// (specialist advisors, non-app trustees) are present in the list but
// cannot be assigned an in-app signature slot — they sign via the
// external-signing flow.

export interface TrusteeRosterView {
  id: string;
  userId?: string;
  displayName: string;
  role: string;
  tag: string;
  tagVariant: 'you' | 'spec' | 'default';
  initials: string;
  avColor: string;
  displayOrder: number;
  status: 'active' | 'resigned' | 'removed';
  appointedAt: string;
  resignedAt?: string;
  /** How this row stands against the trust's CURRENT Letter of Authority
   *  (Beno, 2026-08-01). Derived server-side at read time, never stored.
   *  Present only on ACTIVE rows and only when `loaApplied` is true.
   *
   *  'undetermined' is a real answer, not a missing one: the letter routinely
   *  prints initials, so "A. van der Merwe" against "Anna van der Merwe" is
   *  plausible and must never be reported as an absence. */
  loaStanding?: 'on-current-letter' | 'not-on-current-letter' | 'undetermined';
}

export interface TrustTrusteesResponse {
  trustees: TrusteeRosterView[];
  /** True when a usable current Letter of Authority was read and the rows carry
   *  a standing. FALSE MEANS "no letter to judge against" — it does NOT mean
   *  everyone is current. Render the roster plainly in that case. */
  loaApplied?: boolean;
  correlationId: string;
}

/** @param opts.enabled Pass `false` to skip the call. Serves the `people`
 *  surface, which a beneficiary-tier member may not read. */
export function useTrustTrustees(
  trustId: string | undefined,
  opts: { enabled?: boolean } = {},
) {
  return useQuery<TrustTrusteesResponse>({
    queryKey: ['trusts', 'trustees', trustId ?? null],
    queryFn: ({ signal }) =>
      api<TrustTrusteesResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId!)}/trustees`,
        { signal },
      ),
    enabled: (opts.enabled ?? true) && !!trustId,
    retry: 1,
  });
}

// ── Per-trust members + access tier (Multi-member End-Client Access) ────────
//
// Wraps:
//   - `GET   /v1/trusts/{trustId}/members`          — the deed roster joined to
//     portal-access status + tier (backs the "People & access" tab).
//   - `PATCH /v1/trusts/{trustId}/members/{userId}` — change a portal member's
//     access tier, or revoke their portal membership entirely.
// See `backend/src/handlers/trust-members-list.ts` and
// `trust-member-set-tier.ts`. The PATCH is step-up gated server-side (a
// membership/tier change is a substantive governance change) — the global
// `StepUpInterceptor` + `StepUpModalHost` catch a `STEP_UP_REQUIRED` 403, run
// MFA in place, and replay the mutation, so the caller only handles
// `ApiError.isStepUpRequired` by declining to surface it as an error.

export interface TrustMemberView {
  /** The active deed-roster entry id, or null for a portal member with no
   *  matching active roster row. */
  rosterId: string | null;
  /** The member's Cosmos user id, or null for a roster entry with no in-app
   *  account (a non-portal trustee — cannot be tier-changed / revoked). */
  userId: string | null;
  displayName: string | null;
  rosterRole: string | null;
  /** 'invited' — a pending portal invitee: the invitation has been sent but
   *  not accepted yet. `rosterId` is null and `userId` is the backend
   *  user-doc id (not a Supabase sub). Rendered read-only — no tier change,
   *  no revoke, no approve until they accept. */
  portalStatus: 'active' | 'none' | 'invited';
  /** The member's access tier on this trust. Null when they have no portal
   *  membership (`portalStatus: 'none'`). For an 'invited' row: the tier the
   *  invite was issued at (applied when they accept). */
  tier: 'trustee' | 'beneficiary' | null;
  /** Whether the member has cleared the firm's verify-for-acting gate, or null
   *  when not a portal member / the firm setting is not in play. */
  verifiedForActing: boolean | null;
}

export interface TrustMembersResponse {
  members: TrustMemberView[];
  correlationId: string;
}

/** GET /v1/trusts/{trustId}/members — the trust's deed roster joined to
 *  portal-access status + tier. Read-only; visible to any trust member + any
 *  tenant-wide reader. Mirrors `useTrust`'s style. */
export function useTrustMembers(trustId: string | undefined) {
  return useQuery<TrustMembersResponse>({
    queryKey: ['trusts', 'members', trustId ?? null],
    queryFn: ({ signal }) =>
      api<TrustMembersResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId!)}/members`,
        { signal },
      ),
    enabled: !!trustId,
  });
}

export interface SetMemberTierInput {
  trustId: string;
  userId: string;
  /** Change the member's tier. Provide EXACTLY ONE of `tier` / `revoke` /
   *  `verifiedForActing` — never combine them in a single call. */
  tier?: 'trustee' | 'beneficiary';
  /** Remove the member's portal membership entirely (mirror trustees-resign).
   *  Provide EXACTLY ONE of `tier` / `revoke` / `verifiedForActing`.
   *
   *  ⚠ NOT portal access only. When the member holds an ACTIVE entry on the
   *  trust's roster, this ALSO takes them off the roster, which is a change to
   *  who may act for the trust. See `removalReason`. */
  revoke?: boolean;
  /** PPL-04 / TEA-11, ruling 7 (2026-09-03): the "documented elsewhere"
   *  override on a roster removal. The backend records a roster removal once the
   *  trust's CURRENT Letter of Authority no longer names the person; when it
   *  still does (or none is on file) it refuses with 409
   *  `ROSTER_REMOVAL_NEEDS_LOA` and a sentence to render as-is. Sending the
   *  operator's typed reason instead clears it and writes those words to the
   *  audit row. Only valid alongside `revoke`, and at least 12 characters. */
  removalReason?: string;
  /** Mark the member as verified for acting (writer-only — firm admin; a
   *  member cannot self-verify). Provide EXACTLY ONE of `tier` / `revoke` /
   *  `verifiedForActing`. */
  verifiedForActing?: true;
}

export interface SetMemberTierResponse {
  ok: true;
  correlationId: string;
}

/**
 * PATCH /v1/trusts/{trustId}/members/{userId} — change a portal member's access
 * tier, revoke their portal membership, or mark them verified for acting.
 * Step-up gated server-side.
 *
 * Errors the caller handles:
 *   - 409 `LAST_TRUSTEE` — cannot remove/downgrade the last trustee-tier member;
 *      appoint or upgrade another trustee first.
 *   - `ApiError.isStepUpRequired` — the global step-up modal handles + replays;
 *      the caller declines to surface it as an error.
 *   - 403 — `verifiedForActing` is writer-only (firm admin); a trustee-tier
 *      member calling it is rejected server-side. The caller should gate the
 *      affordance on role so this shouldn't normally be reached, but it is
 *      handled gracefully via the standard error toast.
 *
 * Invalidates the trust's members list + the trust detail (its `callerTier`
 * changes if the caller changed their own tier).
 */
export function useSetMemberTier() {
  const qc = useQueryClient();
  return useMutation<SetMemberTierResponse, ApiError, SetMemberTierInput>({
    mutationFn: ({ trustId, userId, tier, revoke, verifiedForActing, removalReason }) =>
      api<SetMemberTierResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId)}/members/${encodeURIComponent(userId)}`,
        {
          method: 'PATCH',
          body: revoke
            ? // The backend refuses `removalReason` on anything but a removal
              // (zod `.strict()` plus a refine), so it is spread ONLY here and
              // only when the operator actually typed one.
              { revoke: true, ...(removalReason ? { removalReason } : {}) }
            : verifiedForActing
              ? { verifiedForActing: true }
              : { tier },
        },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['trusts', 'members', vars.trustId] });
      void qc.invalidateQueries({ queryKey: ['trusts', 'detail', vars.trustId] });
    },
  });
}

// ── PII masking helper ─────────────────────────────────────────────────────

/** Mask an AE-decrypted PII string, showing only the last 4 chars.
 *  Returns `••••••<last4>` for inputs longer than 4 chars; the input as-is
 *  if 4 chars or fewer; `'None'` for nullish values so the UI can render a
 *  stable placeholder without conditional JSX. Used for masking idNumber,
 *  taxNumber, passportNumber on the beneficiary card render. */
export function maskPii(value: string | null | undefined): string {
  if (!value) return 'None';
  if (value.length <= 4) return value;
  return `••••••${value.slice(-4)}`;
}

// ── Batch 18 / A3 (2026-05-20) — B2C trustee asset-entry mutations ──────────
//
// Three trust-scoped POST endpoints lets direct-onboarded trustees record
// the three asset classes that previously had no UI:
//   - POST /v1/trusts/{trustId}/properties     (Camps Bay residence, etc.)
//   - POST /v1/trusts/{trustId}/companies      (Pty Ltd shareholdings)
//   - POST /v1/trusts/{trustId}/jse-holdings   (JSE listed equity)
//
// Each mutation invalidates the dashboard holdings query keys so the
// Portfolios screen picks up the new row on the next render.

export interface AddPropertyRequest {
  trustId: string;
  description: string;
  erfNumber: string;
  titleDeedNumber: string;
  marketValueRand: number;
  address: string;
  acquiredAt?: string;
}

export interface AddedHolding {
  id: string;
  trustId: string;
  assetClass: string;
  assetName: string;
  asAt: string;
  createdAt: string;
  [extra: string]: unknown;
}

export interface AddPropertyResponse {
  holding: AddedHolding;
  correlationId: string;
}

export function useAddTrustProperty() {
  const qc = useQueryClient();
  return useMutation<AddPropertyResponse, ApiError, AddPropertyRequest>({
    mutationFn: ({ trustId, ...body }) =>
      api<AddPropertyResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId)}/properties`,
        { method: 'POST', body },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['dashboard', 'holdings', vars.trustId] });
      void qc.invalidateQueries({ queryKey: ['dashboard', 'allocation', vars.trustId] });
      // The "My trusts" list rollup (`useTrusts` → `['trusts','list']`) is a
      // separate query from the dashboard widgets above — without this it
      // keeps showing the pre-add Rand total until staleTime lapses.
      void qc.invalidateQueries({ queryKey: ['trusts', 'list'] });
    },
  });
}

export interface AddCompanyRequest {
  trustId: string;
  name: string;
  cipcNumber: string;
  shareholdingPct: number;
  marketValueRand: number;
  industry?: string;
  acquiredAt?: string;
}

export interface AddCompanyResponse {
  holding: AddedHolding;
  correlationId: string;
}

export function useAddTrustCompany() {
  const qc = useQueryClient();
  return useMutation<AddCompanyResponse, ApiError, AddCompanyRequest>({
    mutationFn: ({ trustId, ...body }) =>
      api<AddCompanyResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId)}/companies`,
        { method: 'POST', body },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['dashboard', 'holdings', vars.trustId] });
      void qc.invalidateQueries({ queryKey: ['dashboard', 'allocation', vars.trustId] });
      // See useAddTrustProperty — the trust-list rollup is a separate query.
      void qc.invalidateQueries({ queryKey: ['trusts', 'list'] });
    },
  });
}

export interface AddJseHoldingRequest {
  trustId: string;
  ticker: string;
  name: string;
  units: number;
  costBasisRand: number;
  currentValueRand: number;
  acquiredAt?: string;
}

export interface AddJseHoldingResponse {
  holding: AddedHolding;
  correlationId: string;
}

export function useAddTrustJseHolding() {
  const qc = useQueryClient();
  return useMutation<AddJseHoldingResponse, ApiError, AddJseHoldingRequest>({
    mutationFn: ({ trustId, ...body }) =>
      api<AddJseHoldingResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId)}/jse-holdings`,
        { method: 'POST', body },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['dashboard', 'holdings', vars.trustId] });
      void qc.invalidateQueries({ queryKey: ['dashboard', 'allocation', vars.trustId] });
      // See useAddTrustProperty — the trust-list rollup is a separate query.
      void qc.invalidateQueries({ queryKey: ['trusts', 'list'] });
    },
  });
}

// ── FIC subject search (B-18) ───────────────────────────────────────────────
// GET /v1/trusts/{trustId}/subjects?q= — searches the BO + beneficiary
// registers for the FIC report subject picker. FIC-authorised roles only.

export interface TrustSubject {
  recordId: string;
  name: string;
  /** ID number masked to its last 4 digits, or null when none on file. */
  maskedId: string | null;
  source: 'beneficial-owner' | 'beneficiary';
  /** TPCA category for beneficial-owner rows. */
  category?: string;
}

export interface TrustSubjectsResponse {
  subjects: TrustSubject[];
  correlationId: string;
}

export function useTrustSubjects(opts: { trustId: string; q: string; enabled?: boolean }) {
  return useQuery<TrustSubjectsResponse>({
    queryKey: ['trusts', 'subjects', opts.trustId || null, opts.q],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (opts.q) params.set('q', opts.q);
      const qs = params.toString();
      return api<TrustSubjectsResponse>(
        `/api/v1/trusts/${encodeURIComponent(opts.trustId)}/subjects${qs ? `?${qs}` : ''}`,
        { signal },
      );
    },
    enabled: (opts.enabled ?? true) && !!opts.trustId,
  });
}

// ── Edit / delete a trustee-recorded holding (A3) ───────────────────────────
// Properties, (Pty) Ltd holdings and JSE equity all live in portfolio_holdings,
// so a single pair of hooks serves the per-row edit + delete actions on all
// three asset surfaces. Both are step-up gated server-side.

export interface DeleteHoldingInput { trustId: string; holdingId: string }

export function useDeleteTrustHolding() {
  const qc = useQueryClient();
  return useMutation<{ id: string; deletedAt?: string }, ApiError, DeleteHoldingInput>({
    mutationFn: ({ trustId, holdingId }) =>
      api<{ id: string; deletedAt?: string }>(
        `/api/v1/trusts/${encodeURIComponent(trustId)}/holdings/${encodeURIComponent(holdingId)}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['dashboard', 'holdings', vars.trustId] });
      void qc.invalidateQueries({ queryKey: ['dashboard', 'allocation', vars.trustId] });
      // See useAddTrustProperty — the trust-list rollup is a separate query.
      void qc.invalidateQueries({ queryKey: ['trusts', 'list'] });
    },
  });
}

export interface PatchHoldingInput {
  trustId: string;
  holdingId: string;
  patch: { assetName?: string; marketValueRand?: number };
}

export function usePatchTrustHolding() {
  const qc = useQueryClient();
  return useMutation<{ holding: { id: string } }, ApiError, PatchHoldingInput>({
    mutationFn: ({ trustId, holdingId, patch }) =>
      api<{ holding: { id: string } }>(
        `/api/v1/trusts/${encodeURIComponent(trustId)}/holdings/${encodeURIComponent(holdingId)}`,
        { method: 'PATCH', body: patch },
      ),
    onSuccess: () => {
      // Broad `['dashboard']` prefix (matches `setup.ts`'s confirm-mutation
      // pattern) — cascades to `['dashboard','summary']` (the hero) too, not
      // just the holdings/allocation widgets on this screen.
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      // The "My trusts" list rollup (`useTrusts` → `['trusts','list']`) is a
      // separate query from `['dashboard', ...]` — without this it kept
      // showing the pre-edit Rand total even though the trust's own Overview
      // (bound to `['dashboard']`) was already fresh.
      void qc.invalidateQueries({ queryKey: ['trusts', 'list'] });
    },
  });
}

// ── Revalue (A4) + valuation history (A1) ───────────────────────────────────
// POST /v1/trusts/{id}/holdings/{hid}/revalue APPENDS a new valuation row
// (history-preserving — unlike PATCH which overwrites), recomputes the trust
// rollup, and is step-up gated server-side. GET .../valuations returns the
// asset's valuation timeline + derived last-valued / next-due / source / stale.

export type ValuationSource = 'manual' | 'imported-csv' | 'broker-api' | 'valuation-feed';

/** WHAT a valuation number is based on (human provenance) — Valuations Wave 1.
 *  Distinct from `ValuationSource` (the machine channel): a manual entry can be
 *  an owner estimate, a municipal valuation, or a professional appraisal.
 *  Optional everywhere — legacy rows never carry it (see `inferValuationBasis`). */
export type ValuationBasis =
  | 'owner-estimate'
  | 'purchase-price'
  | 'afs'
  | 'municipal-valuation'
  | 'indexed-estimate'
  | 'market-price'
  | 'avm'
  | 'professional-appraisal';

/** Human label for a valuation basis. PURE. */
export function valuationBasisLabel(b: ValuationBasis): string {
  switch (b) {
    case 'owner-estimate':
      return 'Owner estimate';
    case 'purchase-price':
      return 'Purchase price';
    case 'afs':
      return 'AFS';
    case 'municipal-valuation':
      return 'Municipal valuation';
    case 'indexed-estimate':
      return 'Indexed estimate';
    case 'market-price':
      return 'Market price';
    case 'avm':
      return 'AVM';
    case 'professional-appraisal':
      return 'Appraisal';
  }
}

/** Legacy-row basis inference (spec §2.1): rows minted before the `basis`
 *  field display as `'avm'` when they came off a valuation feed, else
 *  `'owner-estimate'`. PURE. */
export function inferValuationBasis(
  source: ValuationSource | undefined,
  basis: ValuationBasis | undefined | null,
): ValuationBasis {
  if (basis) return basis;
  return source === 'valuation-feed' ? 'avm' : 'owner-estimate';
}

export interface RevalueHoldingInput {
  trustId: string;
  holdingId: string;
  /** New market value in whole Rands. */
  marketValueRand: number;
  /** Valuation date (YYYY-MM-DD); defaults server-side to today. */
  asAt?: string;
  /** What the number is based on — defaults server-side to 'owner-estimate'. */
  basis?: ValuationBasis;
  /** Explicit backfill intent: this is a HISTORICAL value (past asAt) that must
   *  never become "latest" by a same-day createdAt tiebreak. The row is stamped
   *  `retrospective: true` (§8.3). */
  backfill?: boolean;
}

export interface RevalueHoldingResponse {
  holding: {
    id: string;
    assetClass: string;
    assetName: string;
    marketValueRand: number;
    previousValueRand: number;
    asAt: string;
    source: ValuationSource;
  };
  correlationId: string;
}

export function useRevalueTrustHolding() {
  const qc = useQueryClient();
  return useMutation<RevalueHoldingResponse, ApiError, RevalueHoldingInput>({
    mutationFn: ({ trustId, holdingId, ...body }) =>
      api<RevalueHoldingResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId)}/holdings/${encodeURIComponent(holdingId)}/revalue`,
        { method: 'POST', body },
      ),
    onSuccess: (_data, vars) => {
      // Broad `['dashboard']` prefix — cascades to `['dashboard','summary']`
      // (the hero) as well as holdings/allocation, matching `setup.ts`'s
      // established confirm-mutation pattern. Without this the hero kept
      // showing the pre-revalue figure for up to `staleTime` (30s).
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['trusts', 'holding-valuations', vars.trustId] });
      // The "My trusts" list rollup is a separate query key — see
      // usePatchTrustHolding above for why this is needed too.
      void qc.invalidateQueries({ queryKey: ['trusts', 'list'] });
    },
  });
}

export interface ValuationSeriesPoint {
  holdingId: string;
  asAt: string;
  marketValueRand: number;
  source: ValuationSource;
  valuationRef?: string;
  byUserId: string;
  recordedAt: string;
  /** Human provenance (Wave 1). Absent on legacy rows — display infers via
   *  `inferValuationBasis(source, basis)`. */
  basis?: ValuationBasis;
  /** True when the point was written under a firm act-as session — the client
   *  sees "Updated by your adviser" (§8.2, never silent). */
  viaFirmActAs?: boolean;
  /** True when the point was created with backfill intent (a historical value
   *  recorded after the fact) — history UI shows "added retrospectively" (§8.3). */
  retrospective?: boolean;
}

export interface HoldingValuationsResponse {
  asset: { assetClass: string; assetName: string };
  latestHoldingId: string;
  lastValuedAt: string | null;
  nextDueAt: string | null;
  latestSource: ValuationSource;
  /** Basis of the LATEST point (Wave 1). Absent from an old backend — infer. */
  latestBasis?: ValuationBasis;
  stale: boolean;
  series: ValuationSeriesPoint[];
  correlationId: string;
}

export function useHoldingValuations(opts: { trustId: string; holdingId: string; enabled?: boolean }) {
  return useQuery<HoldingValuationsResponse>({
    queryKey: ['trusts', 'holding-valuations', opts.trustId, opts.holdingId],
    queryFn: ({ signal }) =>
      api<HoldingValuationsResponse>(
        `/api/v1/trusts/${encodeURIComponent(opts.trustId)}/holdings/${encodeURIComponent(opts.holdingId)}/valuations`,
        { signal },
      ),
    enabled: (opts.enabled ?? true) && !!opts.trustId && !!opts.holdingId,
  });
}

// ── §16 recognised assets (Wave C / F1–F2; ESTATE model 2026-07-11) ─────────
//
// The §16 asset feature treats a property / vehicle / company-shareholding /
// investment recognised from the onboarding documents as a FIRST-CLASS record
// (an AssetDoc), distinct from the flattened `portfolio_holdings` rollup. An
// asset can be OWNED by a trust, by a company the trust holds, or simply sit
// in the client's personal ESTATE with no trust intent — the ESTATE
// asset-ownership model (2026-07-11 Paul ruling,
// tasks/estate-ownership-20260711/DESIGN.md) gives it a 3-state ownership
// state (see `AssetOwnershipState` below):
//   - `registered_to_trust`  → "Owned — registered to the trust" (the
//      registered-owner string names this trust/company). The ONLY state
//      that projects into holdings/AUM/totalValue.
//   - `earmarked_for_trust`  → "Earmarked — transfer to trust in progress"
//      (the owner string names a person / third party / nothing we hold;
//      moving title is a legal process, not a record change). EXACT
//      byte-for-byte semantics of the retired `claimed_pending_registration`
//      value — only the wire string + display copy changed.
//   - `person_owned`         → "Personal estate" — in the client's estate, no
//      trust intent declared. NEW state; nothing on this branch PRODUCES it
//      yet (a later phase's write paths assign it).
//
// Backend (Wave B, live): `GET /v1/trusts/{trustId}/assets`,
// `GET /v1/companies/{companyId}/assets`, `POST /v1/assets/{id}/assign`,
// `PATCH /v1/assets/{id}`. `valueZar` is in CENTS (use `formatZarCents`).

/** The §16 asset taxonomy (mirrors `AssetDoc.assetType`). */
export type AssetType =
  | 'property'
  | 'vehicle'
  | 'company-shares'
  | 'investment'
  | 'other';

/** ESTATE asset-ownership model (2026-07-11 Paul ruling — mirrors
 *  `AssetDoc.ownershipState`; see `tasks/estate-ownership-20260711/DESIGN.md`).
 *  Three states, container = the client's tenant-level ESTATE:
 *    - `person_owned`        — in the estate, no trust intent. NEW state;
 *      nothing PRODUCES this yet (assigned by write paths in a later phase).
 *    - `earmarked_for_trust` — intent declared; EXACT byte-for-byte semantics
 *      of the legacy `claimed_pending_registration` value (anchored to a
 *      trust, never projected into holdings).
 *    - `registered_to_trust` — document evidence; the ONLY state that
 *      projects into holdings/AUM/totalValue. */
export type AssetOwnershipState =
  | 'registered_to_trust'
  | 'earmarked_for_trust'
  | 'person_owned';

/** The owner entity kind an asset points at.
 *
 *  Widened to include `'person'` (ESTATE ownership model, Wave H,
 *  2026-07-11): the un-earmark / "keep it personal" action moves an asset OUT
 *  of any trust anchor and into the client's ESTATE — `ownerEntityType:
 *  'person'` with NO `ownerEntityId` (there is no person-entity table; the
 *  person is the display-level `registeredOwnerString`). Every consumer that
 *  narrowed this union to `'trust' | 'company'` (the assign-dialog target
 *  list, `asset-belongs-to.tsx`'s `targetValue()`) was fixed alongside this
 *  widening — see `useAssignAsset` below for the discriminated-union input
 *  this drives. */
export type AssetOwnerEntityType = 'trust' | 'company' | 'person';

/** API projection of an AssetDoc (mirrors `toAssetView` in the backend
 *  `assets-assign.ts`). `valueZar` is CENTS. Optional fields are present only
 *  when set on the doc. */
export interface AssetView {
  id: string;
  assetType: AssetType;
  description: string;
  /** ERF / title-deed / CIPC / VIN / policy number — null when none on file. */
  identifier: string | null;
  /** Round-3 grader Row 12: the asset's model/vintage year (e.g. a vehicle's
   *  year of manufacture). Absent when not captured — never fabricated. */
  year?: number;
  /** The owner exactly as named on the source document (already PII-scrubbed
   *  server-side). Drives the owned-vs-pending verdict. */
  registeredOwnerString: string | null;
  ownershipState: AssetOwnershipState;
  /** AST-04: present on an asset a FIRM recorded. `pending-trustee` until a
   *  trustee of the trust confirms it (`usePatchAsset` with `trusteeConfirmed`). */
  confirmation?: {
    status: 'pending-trustee' | 'confirmed';
    recordedAt: string;
    recordedViaFirmTenantId?: string;
    confirmedByUserId?: string;
    confirmedAt?: string;
  };
  ownerEntityType?: AssetOwnerEntityType;
  ownerEntityId?: string;
  /** Market value in CENTS (Rand×100). */
  valueZar: number;
  /** DISPLAY-ONLY short name the owner chose, e.g. "Property 1" (Beno,
   *  2026-08-01). NEVER a substitute for `description`, which stays the
   *  official registered name used for SARS and annual financial statements.
   *  Absent when the owner has not set one. */
  nickname?: string;
  /** Managing agent for a property — who to call about it. Absent when not
   *  captured. */
  managingAgent?: {
    name?: string;
    company?: string;
    phone?: string;
    email?: string;
  };
  /** Shareholding percentage for `company-shares` assets (0–100). */
  sharePct?: number;
  evidenceDocumentId?: string;
  /** The document this asset was EXTRACTED from, resolved server-side (2026-07-30).
   *  Beno asked of a real property on his own trust: "where is this from pulled,
   *  I did not add it?" — the id above was always stored, but an opaque id
   *  answers nothing, so an extracted asset looked identical to a phantom.
   *  Absent for a manually-added asset, and absent when the source document is
   *  no longer on file (the backend never fabricates one). */
  evidenceDocument?: {
    id: string;
    filename: string;
    documentType: string;
    uploadedAt: string;
  };
  /** Rubric row 13: ADDITIONAL ownership-evidence links (a property-transfer
   *  email attached on address match, or a manual attach) — drives the
   *  "Attached from transfer email" provenance chip + one-click detach. Absent
   *  when the asset has no additional evidence. */
  ownershipEvidenceDocumentIds?: AssetEvidenceLink[];
  createdAt: string;
  updatedAt?: string;
}

/** One additional ownership-evidence link on an asset (mirrors the backend
 *  `AssetEvidenceLink`). */
export interface AssetEvidenceLink {
  documentId: string;
  source: 'transfer-email' | 'manual';
  attachedByUserId: string;
  attachedAt: string;
}

export interface AssetsListResponse {
  assets: AssetView[];
  correlationId: string;
}

/** Read-boundary shim (ESTATE asset-ownership model,
 *  `tasks/estate-ownership-20260711/DESIGN.md`) mirroring the backend's
 *  `normalizeOwnershipState()` (`asset-repository.ts`). During a mixed
 *  dev/prod deploy window an OLD backend build may still serve the retired
 *  `claimed_pending_registration` wire value on this response — normalise it
 *  here so the rest of the FE only ever handles the current 3-state
 *  `AssetOwnershipState` union. `registered_to_trust` / `person_owned` rows
 *  pass through unchanged (same reference). PURE. */
function normalizeAssetView(a: AssetView): AssetView {
  if ((a.ownershipState as string) === 'claimed_pending_registration') {
    return { ...a, ownershipState: 'earmarked_for_trust' };
  }
  return a;
}

/** Key + fetcher shared with the auth-time dashboard prefetch
 *  (`prefetch-dashboard.ts`) — one definition, so the prefetched cache entry
 *  (including the `normalizeAssetView` mapping) is byte-identical to what the
 *  wealth-distribution + registration-journey cards read. */
export function trustAssetsQueryKey(trustId: string | undefined) {
  return ['trusts', 'assets', trustId ?? null] as const;
}

export async function fetchTrustAssets(
  trustId: string,
  signal?: AbortSignal,
): Promise<AssetsListResponse> {
  const res = await api<AssetsListResponse>(
    `/api/v1/trusts/${encodeURIComponent(trustId)}/assets`,
    { signal },
  );
  return { ...res, assets: res.assets.map(normalizeAssetView) };
}

/** GET /v1/trusts/{trustId}/assets — the §16 assets owned by a trust. Cloned
 *  from `useDashboardHoldings`; the backend degrades to `[]` on a not-yet-
 *  provisioned `assets` container (a 200 empty list, not a 5xx). */
export function useTrustAssets(trustId: string | undefined, opts: { enabled?: boolean } = {}) {
  return useQuery<AssetsListResponse>({
    queryKey: trustAssetsQueryKey(trustId),
    queryFn: ({ signal }) => fetchTrustAssets(trustId!, signal),
    enabled: (opts.enabled ?? true) && !!trustId,
  });
}

/** GET /v1/companies/{companyId}/assets — the §16 assets owned by a company a
 *  trust holds (the trust → company → asset chain). */
export function useCompanyAssets(companyId: string | undefined, opts: { enabled?: boolean } = {}) {
  return useQuery<AssetsListResponse>({
    queryKey: ['companies', 'assets', companyId ?? null],
    queryFn: async ({ signal }) => {
      const res = await api<AssetsListResponse>(
        `/api/v1/companies/${encodeURIComponent(companyId!)}/assets`,
        { signal },
      );
      return { ...res, assets: res.assets.map(normalizeAssetView) };
    },
    enabled: (opts.enabled ?? true) && !!companyId,
  });
}

/** A recognised company (mirrors the backend `CompanyDoc`, tenantId + Cosmos
 *  metadata stripped) — one record per company a trust owns/controls,
 *  recognised from a CIPC document (MOI / COR form / share register) at
 *  confirm or entered manually. NOT the same data source as the holdings-based
 *  "Companies" tab (`useAddTrustCompany` / private-equity shareholdings) — this
 *  is the CIPC-recognition surface. */
/** One row of a company's share register (SHARED CONTRACT, Paul-ruled
 *  2026-07-13). Pilot scope is trust-only, so `shareholders[]` carries at most
 *  the holding trust's row; person / other-company co-shareholders are a
 *  fast-follow. `shareholdingPct` is the count-derived value (owned ÷ issued),
 *  or a typed-% fallback when the issued total is unknown. */
export interface CompanyShareholder {
  ownerType: 'trust' | 'person' | 'company';
  ownerId: string;
  ownerName: string;
  /** Shares this owner holds (int). Absent when captured as a typed % only. */
  sharesOwned?: number;
  /** Ownership percentage — derived from counts, or the typed-% fallback. */
  shareholdingPct?: number;
}

export interface CompanyView {
  id: string;
  name: string;
  cipcRegistration: string | null;
  industry?: string;
  heldByTrustId?: string;
  trustSharePct?: number;
  registeredOwnerString?: string | null;
  /** SHARED CONTRACT (share register, 2026-07-13). MOI-ceiling authorised share
   *  total (int). Rarely on a CoR15.1A — usually manual / a securities register. */
  authorisedShares?: number;
  /** Total allotted (issued) shares (int) — the denominator the trust's
   *  shareholding % is derived against. */
  issuedShares?: number;
  /** ONE operator-set company valuation in CENTS. Each shareholder's stake value
   *  derives from `shareholdingPct × companyValuationCents`. Absent = unvalued
   *  ("valuation pending"). */
  companyValuationCents?: number;
  /** The company's shareholders. Pilot: trust-only (≤1 row). */
  shareholders?: CompanyShareholder[];
  createdAt: string;
  updatedAt?: string;
}

export interface CompaniesListResponse {
  companies: CompanyView[];
  correlationId: string;
}

export interface CompanyGetResponse {
  company: CompanyView;
  correlationId: string;
}

/** GET /v1/companies — recognised companies, optionally scoped to one trust.
 *  Mirrors `useTrustAssets`'s degrade contract: a not-yet-provisioned
 *  `companies` container or no recognised companies both return a 200 empty
 *  list, not a 5xx. */
export function useCompanies(opts: { trustId?: string; enabled?: boolean } = {}) {
  return useQuery<CompaniesListResponse>({
    queryKey: ['companies', 'list', opts.trustId ?? null],
    queryFn: ({ signal }) =>
      api<CompaniesListResponse>(
        `/api/v1/companies${opts.trustId ? `?trustId=${encodeURIComponent(opts.trustId)}` : ''}`,
        { signal },
      ),
    enabled: opts.enabled ?? true,
  });
}

/** GET /v1/companies/{companyId} — a single recognised company. */
export function useCompany(companyId: string | undefined, opts: { enabled?: boolean } = {}) {
  return useQuery<CompanyGetResponse>({
    queryKey: ['companies', 'detail', companyId ?? null],
    queryFn: ({ signal }) =>
      api<CompanyGetResponse>(`/api/v1/companies/${encodeURIComponent(companyId!)}`, { signal }),
    enabled: (opts.enabled ?? true) && !!companyId,
  });
}

/**
 * PATCH /v1/companies/{companyId} — update the share-register fields on a
 * recognised company (SHARED CONTRACT, Paul-ruled 2026-07-13). Backs the
 * company detail screen's inline-edit saves: the operator sets the issued /
 * authorised share totals, the trust's shares owned, and ONE company valuation
 * (whole Rand → cents at the call site). Every field is optional — a save
 * carries only what changed.
 *
 * SHARED CONTRACT field names (backend PATCH handler mirrors these exactly):
 *   authorisedShares?, issuedShares?, companyValuationCents?, sharesOwned?
 * (`sharesOwned` updates the holding trust's shareholder row; the backend
 * re-derives `shareholdingPct` + the company-shares AssetDoc.valueZar).
 */
export interface UpdateCompanyShareRegisterRequest {
  companyId: string;
  /** The holding trust — used to invalidate its dashboard rollup on success. */
  trustId?: string;
  authorisedShares?: number;
  issuedShares?: number;
  companyValuationCents?: number;
  sharesOwned?: number;
}

export interface UpdateCompanyShareRegisterResponse {
  company: CompanyView;
  correlationId: string;
}

export function useUpdateCompanyShareRegister() {
  const qc = useQueryClient();
  return useMutation<
    UpdateCompanyShareRegisterResponse,
    ApiError,
    UpdateCompanyShareRegisterRequest
  >({
    mutationFn: ({ companyId, trustId: _trustId, ...body }) =>
      api<UpdateCompanyShareRegisterResponse>(
        `/api/v1/companies/${encodeURIComponent(companyId)}`,
        { method: 'PATCH', body },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['companies', 'detail', vars.companyId] });
      void qc.invalidateQueries({ queryKey: ['companies', 'list'] });
      // A valuation / shares change moves the trust's private-equity rollup +
      // the "My trusts" list total — invalidate both like the add-company path.
      if (vars.trustId) {
        void qc.invalidateQueries({ queryKey: ['dashboard', 'holdings', vars.trustId] });
        void qc.invalidateQueries({ queryKey: ['dashboard', 'allocation', vars.trustId] });
      }
      void qc.invalidateQueries({ queryKey: ['trusts', 'list'] });
    },
  });
}

/** Generate a fresh idempotency key per assign attempt. The assign endpoint is
 *  `withVerifiedIdempotency`-wrapped, so a double-submit replays server-side
 *  rather than re-deriving the ownership a second time. */
function newAssetIdempotencyKey(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `assign-asset-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** ESTATE ownership model (2026-07-11 ruling): a discriminated union mirroring
 *  the backend's `AssignAssetSchema` — the `'person'` variant carries NO
 *  `ownerEntityId` (there is no person-entity table; a caller-supplied id for
 *  a person target would be a client bug, not silently-ignored input). This is
 *  the un-earmark / "keep it personal" action. */
export type AssignAssetInput =
  | {
      assetId: string;
      ownerEntityType: 'trust' | 'company';
      ownerEntityId: string;
      /** The asset's CURRENT rollup trust (if any) — used purely to invalidate
       *  the source trust's assets/holdings on a cross-trust move. Not sent. */
      sourceTrustId?: string;
      /** The chosen target's rollup trust (a trust target = itself; a company
       *  target = the company's holding trust) — used to invalidate the
       *  target trust's holdings. Not sent. */
      targetTrustId?: string;
    }
  | {
      assetId: string;
      ownerEntityType: 'person';
      /** The trust the asset was earmarked to BEFORE un-earmarking — used
       *  purely to invalidate that trust's assets/holdings + the dashboard's
       *  earmarked figure. Not sent. */
      sourceTrustId?: string;
      targetTrustId?: string;
    };

export interface AssignAssetResponse {
  asset: AssetView;
  /** True when the asset moved but its registered-owner string does NOT name
   *  the chosen target — the move is recorded but ownership stays
   *  `earmarked_for_trust` (documentation needed to register title). */
  registrationNeeded: boolean;
  correlationId: string;
}

/**
 * ── `useOperatorSession: true` on every §16 asset WRITE (2026-08-27, AST-03) ──
 *
 * The five mutations below MUST travel on the operator's OWN Supabase session,
 * never on an act-as token. This is not an optimisation — it is the difference
 * between the button working and the button being permanently dead, and it is
 * the same fix `useLodgeMaster` carries (beneficial-ownership.ts) for exactly
 * the same reason.
 *
 * A firm user opens a client's trust via act-as, so by default every call from
 * the assets screen rides the firm-act token. But `mintFirmActToken`
 * deliberately never stamps `mfa_challenge_at` (the C2 wall), and all five
 * handlers require step-up — so the write 403s STEP_UP_REQUIRED, the global
 * step-up modal challenges, mints a fresh SUPABASE token, and replays the
 * mutation on the UNCHANGED act-as token, which 403s again. An infinite MFA
 * loop the operator reads as "the button does nothing". `POST /v1/assets` was
 * given its cross-book server path on 26 August and stayed dead for precisely
 * this reason: the server half was built and the client never sent the session
 * that could reach it.
 *
 * Riding the operator session instead means the firm admin's real MFA challenge
 * satisfies step-up, and the backend resolves the asset (or, for create, the
 * trust) across the firm's grant-verified book — `findAssetAcrossBook` /
 * `findTrustAcrossBook` — and writes into the CLIENT tenant on a live `manage`
 * grant. The C2 wall stands untouched, because no act-as token is involved.
 *
 * For a client-tenant user with no act-as active this flag is a no-op: they
 * already are the operator session.
 *
 * ⚠ THE ONE ASYMMETRY. `useCreateAsset` with NO `trustId` is an ESTATE asset,
 * and the server writes an estate asset into the CALLER's own tenant because
 * there is no trust to resolve it through. Under act-as that would silently
 * land a client's asset in the FIRM's estate, so `AssetAddDialog` does not
 * offer the personal-estate option while a firm is administering a client.
 * Every other write here keys on an ASSET id and follows it into the tenant it
 * actually lives in, so none of them can be mis-routed this way.
 */

/**
 * POST /v1/assets/{id}/assign — reassign / move an asset to a trust, a
 * company, or (ESTATE ownership model) UN-EARMARK it back to the client's
 * personal estate (`ownerEntityType: 'person'`, no `ownerEntityId`).
 *
 * Step-up + idempotency gated server-side. The global `StepUpInterceptor` +
 * `StepUpModalHost` catch a `STEP_UP_REQUIRED` failure, run MFA in place, and
 * replay the mutation. The caller renders an honest message for
 * `ApiError.isStepUpRequired` in its catch.
 *
 * Errors the caller handles:
 *   - 409 `COMPANY_OWNED_ASSET` — the asset is held by a company; move the
 *      company (or reassign within it), don't flatten the chain onto a trust.
 *   - 400 `TARGET_NOT_FOUND` — the chosen target isn't in this tenant.
 *   - 409 `PERSON_ASSIGN_CONTRADICTS_DOCUMENT` — un-earmarking to the personal
 *      estate, but the asset's registered-owner string still names a
 *      trust/company this tenant holds. A person-assign must never silently
 *      contradict standing document evidence — reassign it to that entity
 *      instead.
 *
 * Invalidates the source + target trusts' asset lists + holdings projections
 * so both reflect the move on the next render, plus the tenant-wide estate
 * view (`useEstateAssets`) unconditionally — ANY assign can shift value
 * between the three ownership states, which the estate totals always reflect.
 */
export function useAssignAsset() {
  const qc = useQueryClient();
  return useMutation<AssignAssetResponse, ApiError, AssignAssetInput>({
    mutationFn: (input) => {
      const body =
        input.ownerEntityType === 'person'
          ? { ownerEntityType: 'person' as const }
          : { ownerEntityType: input.ownerEntityType, ownerEntityId: input.ownerEntityId };
      return api<AssignAssetResponse>(
        `/api/v1/assets/${encodeURIComponent(input.assetId)}/assign`,
        {
          method: 'POST',
          body,
          headers: { 'idempotency-key': newAssetIdempotencyKey() },
          // See the operator-session note above this block.
          useOperatorSession: true,
        },
      );
    },
    onSuccess: (_data, vars) => {
      // The asset moved — refresh BOTH the source + target asset lists, and the
      // holdings projections (the dashboard donut rolls assets up). The broad
      // `['dashboard']` prefix also cascades to `['dashboard','summary']` (the
      // hero), matching `setup.ts`'s established confirm-mutation pattern.
      void qc.invalidateQueries({ queryKey: ['trusts', 'assets'] });
      void qc.invalidateQueries({ queryKey: ['companies', 'assets'] });
      // ESTATE ownership model: every assign can move value into/out of
      // person_owned or earmarked_for_trust — the estate card + totals always
      // need a refresh, independent of whether a trust was involved.
      void qc.invalidateQueries({ queryKey: ['estate', 'assets'] });
      if (vars.sourceTrustId || vars.targetTrustId) {
        void qc.invalidateQueries({ queryKey: ['dashboard'] });
        // The "My trusts" list rollup is a separate query key from
        // `['dashboard', ...]` — without this the list kept showing stale
        // Rand totals for the source/target trust after a move.
        void qc.invalidateQueries({ queryKey: ['trusts', 'list'] });
      }
    },
  });
}

/**
 * POST /v1/assets — record an asset by hand.
 *
 * Until 2026-08-25 the ONLY two ways an asset came into existence were a
 * document the ingestion matcher read, and the client-mapping wizard. Paul, on
 * the prod walkthrough: "I don't know how to add my own assets. This is a
 * problem."
 *
 * `trustId` earmarks the asset for that trust; omit it to record it in the
 * personal estate. Naming a trust does NOT register the asset to it — that
 * still takes a document, and the server enforces it (assets-create.ts).
 */
export interface CreateAssetInput {
  assetType: AssetType;
  description: string;
  identifier?: string | null;
  year?: number;
  /** WHOLE RAND. The server converts to cents. */
  valueRands?: number;
  nickname?: string | null;
  /** Earmark for this trust; omit for the personal estate. */
  trustId?: string;
  registeredOwnerString?: string | null;
}

export interface CreateAssetResponse {
  asset: AssetView;
  correlationId?: string;
}

export function useCreateAsset() {
  const qc = useQueryClient();
  return useMutation<CreateAssetResponse, ApiError, CreateAssetInput>({
    mutationFn: (input) =>
      api<CreateAssetResponse>('/api/v1/assets', {
        method: 'POST',
        body: input,
        headers: { 'idempotency-key': newAssetIdempotencyKey() },
        // See the operator-session note above `useAssignAsset`, including the
        // estate-asset asymmetry that `AssetAddDialog` handles.
        useOperatorSession: true,
      }),
    onSuccess: (_data, vars) => {
      // Same fan-out as an assign: a new asset changes the trust list, the
      // estate card and the dashboard rollups, not just this one table.
      void qc.invalidateQueries({ queryKey: ['trusts', 'assets'] });
      void qc.invalidateQueries({ queryKey: ['estate', 'assets'] });
      if (vars.trustId) {
        void qc.invalidateQueries({ queryKey: ['dashboard'] });
        void qc.invalidateQueries({ queryKey: ['trusts', 'list'] });
      }
    },
  });
}

export interface PatchAssetInput {
  assetId: string;
  patch: {
    description?: string;
    identifier?: string | null;
    /** WHOLE RAND (the backend converts to cents on write). */
    valueRands?: number;
    /** Round-3 grader Row 12: model/vintage year — see `AssetView.year`. */
    year?: number;
    /** AST-04: a trustee vouching for an asset a firm recorded. Literal true. */
    trusteeConfirmed?: true;
  };
  /** The asset's rollup trust — used to invalidate its holdings projection when
   *  the value/description changes. Not sent. */
  trustId?: string;
}

export interface PatchAssetResponse {
  asset: AssetView;
  correlationId: string;
}

/** PATCH /v1/assets/{id} — edit a recognised asset's description / identifier /
 *  value. Owner + ownership state move via `useAssignAsset`, not here. Step-up +
 *  idempotency gated server-side. */
export function usePatchAsset() {
  const qc = useQueryClient();
  return useMutation<PatchAssetResponse, ApiError, PatchAssetInput>({
    mutationFn: ({ assetId, patch }) =>
      api<PatchAssetResponse>(
        `/api/v1/assets/${encodeURIComponent(assetId)}`,
        {
          method: 'PATCH',
          body: patch,
          headers: { 'idempotency-key': newAssetIdempotencyKey() },
          // See the operator-session note above `useAssignAsset`.
          useOperatorSession: true,
        },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['trusts', 'assets'] });
      void qc.invalidateQueries({ queryKey: ['companies', 'assets'] });
      // A value/description edit changes what the estate totals sum — see
      // useAssignAsset above for why this stays unconditional.
      void qc.invalidateQueries({ queryKey: ['estate', 'assets'] });
      if (vars.trustId) {
        // Broad `['dashboard']` prefix — cascades to `['dashboard','summary']`
        // (the hero) too, matching `setup.ts`'s established confirm-mutation
        // pattern.
        void qc.invalidateQueries({ queryKey: ['dashboard'] });
        // The "My trusts" list rollup is a separate query key — see
        // useAssignAsset above for why this is needed too.
        void qc.invalidateQueries({ queryKey: ['trusts', 'list'] });
      }
    },
  });
}

// ── Rubric row 13: ownership-evidence attach / detach ───────────────────────

export interface AttachEvidenceInput {
  assetId: string;
  /** The vault `documents` id to attach as additional ownership evidence. */
  documentId: string;
  /** The asset's rollup trust — used to invalidate its asset list. Not sent. */
  trustId?: string;
}

export interface EvidenceMutationResponse {
  asset: AssetView;
  correlationId: string;
}

/**
 * POST /v1/assets/{id}/evidence — MANUALLY attach an ownership-evidence document
 * (the manual fallback when the transfer-email auto-attach found no confident
 * match). Step-up + idempotency gated server-side (the global StepUpInterceptor
 * runs MFA in place + replays). Invalidates the asset lists so the provenance
 * chip renders.
 */
export function useAttachAssetEvidence() {
  const qc = useQueryClient();
  return useMutation<EvidenceMutationResponse, ApiError, AttachEvidenceInput>({
    mutationFn: ({ assetId, documentId }) =>
      api<EvidenceMutationResponse>(
        `/api/v1/assets/${encodeURIComponent(assetId)}/evidence`,
        {
          method: 'POST',
          body: { documentId },
          headers: { 'idempotency-key': newAssetIdempotencyKey() },
          // See the operator-session note above `useAssignAsset`.
          useOperatorSession: true,
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['trusts', 'assets'] });
      void qc.invalidateQueries({ queryKey: ['companies', 'assets'] });
    },
  });
}

export interface DetachEvidenceInput {
  assetId: string;
  /** The attached documentId to remove. */
  documentId: string;
  trustId?: string;
}

/**
 * DELETE /v1/assets/{id}/evidence/{documentId} — one-click DETACH of an
 * ownership-evidence link. The underlying document is NOT deleted — only the link
 * is removed. Step-up gated server-side.
 */
export function useDetachAssetEvidence() {
  const qc = useQueryClient();
  return useMutation<EvidenceMutationResponse, ApiError, DetachEvidenceInput>({
    mutationFn: ({ assetId, documentId }) =>
      api<EvidenceMutationResponse>(
        `/api/v1/assets/${encodeURIComponent(assetId)}/evidence/${encodeURIComponent(documentId)}`,
        {
          method: 'DELETE',
          headers: { 'idempotency-key': newAssetIdempotencyKey() },
          // See the operator-session note above `useAssignAsset`. Attach and
          // detach must route identically — a firm that can attach but not
          // detach cannot undo its own mistake.
          useOperatorSession: true,
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['trusts', 'assets'] });
      void qc.invalidateQueries({ queryKey: ['companies', 'assets'] });
    },
  });
}

// ── §16 asset display helpers (pure — unit-tested) ──────────────────────────

/** Human label for an asset type. */
export function assetTypeLabel(t: AssetType): string {
  switch (t) {
    case 'property':
      return 'Property';
    case 'vehicle':
      return 'Vehicle';
    case 'company-shares':
      return 'Company shares';
    case 'investment':
      return 'Investment';
    case 'other':
      return 'Other asset';
  }
}

/** The `Icon` name to render for an asset type (from the shared icon set). */
export function assetTypeIconName(t: AssetType): 'trust' | 'master' | 'cash' | 'doc' {
  switch (t) {
    case 'property':
      return 'trust'; // the shield-house glyph reads as "property/estate"
    case 'company-shares':
      return 'master'; // the building glyph
    case 'investment':
      return 'cash';
    case 'vehicle':
    case 'other':
      return 'doc';
  }
}

/** ESTATE-state presentation for a status badge: the label, a tone, and the
 *  design-system colour vars. `positive` (green) = registered/owned; `pending`
 *  (amber/`--warn`) = earmarked, transfer in progress; `neutral`
 *  (`--surface-2`/`--ink-2`) = person-owned — in the estate, no trust intent,
 *  neither a compliance gap nor a trust asset. PURE. */
export interface AssetStatusBadge {
  label: string;
  tone: 'positive' | 'pending' | 'neutral';
  /** Background + text + border CSS var names for the pill. */
  bg: string;
  fg: string;
}

export function assetStatusBadge(state: AssetOwnershipState): AssetStatusBadge {
  if (state === 'registered_to_trust') {
    return {
      label: 'Owned: registered to the trust',
      tone: 'positive',
      bg: 'var(--positive-soft)',
      fg: 'var(--positive)',
    };
  }
  if (state === 'person_owned') {
    return {
      label: 'Personal estate',
      tone: 'neutral',
      bg: 'var(--surface-2)',
      fg: 'var(--ink-2)',
    };
  }
  // `earmarked_for_trust` — EXACT byte-for-byte semantics of the legacy
  // `claimed_pending_registration` state (ESTATE model, 2026-07-11 ruling);
  // only the copy changed here (Agent A's Work item) — "earmarked" names the
  // estate-ownership intent honestly instead of reading like a compliance
  // chore. Still amber: this is progress, not an error.
  return {
    label: 'Earmarked: transfer to trust in progress',
    tone: 'pending',
    bg: 'var(--warn-soft)',
    fg: 'var(--warn)',
  };
}

// ── build-campaign 2026-07-09 · lane A ───────────────────────────────────────
// FICA s23 declarations (PEP / FATCA / source of funds) — the trust-scoped
// read + re-declare pair. These live here (not in a lane hook file) because
// trusts.ts owns the /v1/app/trusts/{id}/* client surface.

/** Mirror of TrustDoc.ficaDeclarations. `null`s are explicit "declared as
 *  no/none" values; absent fields were never declared. */
export interface TrustFicaDeclarations {
  isPep?: boolean | null;
  isFatca?: boolean | null;
  sourceOfFunds?:
    | 'salary'
    | 'inheritance'
    | 'business'
    | 'sale-of-assets'
    | 'other'
    | null;
  sourceOfFundsOther?: string;
  /** Server-stamped on every (re-)declaration — clients cannot backdate. */
  declaredAt?: string;
}

export interface TrustFicaDeclarationsResponse {
  /** `null` when the trust has never completed a declaration (wizard step
   *  skipped / admin-created client) — render a "complete your declaration"
   *  prompt, not an error (wire-level null convention per the handler). */
  ficaDeclarations: TrustFicaDeclarations | null;
  correlationId: string;
}

/** GET /v1/app/trusts/{trustId}/fica-declarations — members + tenant-wide
 *  readers; no step-up (a compliance declaration read). */
export function useTrustFicaDeclarations(trustId: string | undefined) {
  return useQuery<TrustFicaDeclarationsResponse>({
    queryKey: ['trusts', 'fica-declarations', trustId ?? null],
    queryFn: ({ signal }) =>
      api<TrustFicaDeclarationsResponse>(
        `/api/v1/app/trusts/${encodeURIComponent(trustId!)}/fica-declarations`,
        { signal },
      ),
    enabled: !!trustId,
  });
}

/** PATCH body — strict partial merge (updating only isPep leaves the other
 *  declarations untouched); `declaredAt` is ALWAYS re-stamped server-side. */
export interface PatchTrustFicaDeclarationsRequest {
  isPep?: boolean;
  isFatca?: boolean;
  sourceOfFunds?: 'salary' | 'inheritance' | 'business' | 'sale-of-assets' | 'other';
  sourceOfFundsOther?: string;
}

export interface PatchTrustFicaDeclarationsResponse {
  ficaDeclarations: TrustFicaDeclarations;
  correlationId: string;
}

/** PATCH /v1/app/trusts/{trustId}/fica-declarations — FICA requires periodic
 *  re-declaration; a trustee CAN re-declare their own trust (the RACI sits
 *  with the principals). Members + tenant-wide writers; no step-up.
 *  409 TRUST_UPDATE_CONFLICT on a write race. */
export function usePatchTrustFicaDeclarations(trustId: string) {
  const qc = useQueryClient();
  return useMutation<
    PatchTrustFicaDeclarationsResponse,
    ApiError,
    PatchTrustFicaDeclarationsRequest
  >({
    mutationFn: (body) =>
      api<PatchTrustFicaDeclarationsResponse>(
        `/api/v1/app/trusts/${encodeURIComponent(trustId)}/fica-declarations`,
        { method: 'PATCH', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['trusts', 'fica-declarations', trustId] });
    },
  });
}
// ── end lane A (trusts.ts) ──
// ── build-campaign 2026-07-09 · lane B ───────────────────────────────────────
// One contiguous block per the shared protocol — B2 (create beneficiary),
// B9 (delete trust), B10 (patch meeting agenda). Do not interleave other
// lanes' hooks here.

// B2 — POST /v1/trusts/{trustId}/beneficiaries (backend
// `trusts-beneficiary-create.ts`). The post-onboarding "add another
// beneficiary" path — the wizard seeds the initial register; THIS is the
// manual add from the beneficiaries screen. Body mirrors the backend
// `CreateBeneficiarySchema` (`.strict()` — no extra fields; `kind` defaults to
// 'named' server-side). Step-up gated server-side: the global
// StepUpInterceptor runs MFA in place + replays, so callers only render a
// graceful message for `ApiError.isStepUpRequired`. `idNumber` persists via
// the DET-encrypted beneficiaries column — send plaintext, never pre-mask.

export interface CreateBeneficiaryRequest {
  trustId: string;
  displayName: string;
  relationship: string;
  /** Percentage share (0–100), optional. */
  sharePct?: number;
  /** SA ID number — encrypted at rest server-side; masked at render. */
  idNumber?: string;
}

export interface CreateBeneficiaryResponse {
  beneficiary: BeneficiaryRow;
  correlationId: string;
}

export function useCreateBeneficiary() {
  const qc = useQueryClient();
  return useMutation<CreateBeneficiaryResponse, ApiError, CreateBeneficiaryRequest>({
    mutationFn: ({ trustId, ...body }) =>
      api<CreateBeneficiaryResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId)}/beneficiaries`,
        { method: 'POST', body },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['trusts', 'beneficiaries', vars.trustId],
      });
    },
  });
}

// B9 — DELETE /v1/trusts/{trustId} (backend `trusts-delete.ts`). SOFT delete:
// the trust is tombstoned, its documents keep their trustId and surface in the
// firm-wide vault flagged "archived trust — reassign". Admin-tier roles only
// (admin / compliance / super_admin) — the caller gates the affordance on role
// so a non-admin never sees a button that would 403. 204 no body. The delete
// ceremony (typed-name confirm + pre-emptive step-up) lives in the settings
// screen — this hook is just the wire call.

export interface DeleteTrustRequest {
  trustId: string;
  /** Recorded into the soft-delete envelope; server defaults 'admin-delete'. */
  reason?: string;
}

export function useDeleteTrust() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, DeleteTrustRequest>({
    mutationFn: ({ trustId, reason }) =>
      api<void>(`/api/v1/trusts/${encodeURIComponent(trustId)}`, {
        method: 'DELETE',
        ...(reason ? { body: { reason } } : {}),
      }),
    onSuccess: () => {
      // The trust is gone from the caller's book — refresh the whole family
      // (list, pickers, any cached detail).
      void qc.invalidateQueries({ queryKey: ['trusts'] });
    },
  });
}

// B10 — PATCH /v1/app/trusts/{trustId}/meetings/{meetingId}/agenda (backend
// `trusts-meeting-agenda-patch.ts`). Records per-agenda-item decisions (and
// optionally per-trustee votes) while the meeting is still mutable. SERVER
// status gate: draft/circulating only, locked/signed returns 409. That 409
// is not the only one this endpoint can return: a concurrent edit also 409s
// with `MEETING_CAS_RACE`, so a caller must route by the response's error
// CODE, not by treating every 409 as "finalised". `meeting-detail-modal.tsx`
// (T7, 21 Sep audit wave) is the caller that does this: only
// `MEETING_STATUS_NOT_MUTABLE` renders "this meeting has been finalised",
// `MEETING_CAS_RACE` renders as a reload-and-retry race instead.
// Step-up gated server-side (global interceptor replays).

export interface PatchMeetingAgendaItem {
  /** Position of the target item in `agendaItems[]` (titles aren't unique). */
  index: number;
  /** The binding decision recorded against the item (min 1 char — an empty
   *  decision would dishonestly inflate `resolutionsTabled`). */
  decision?: string;
  /** Per-trustee votes: trusteeUserId → position. */
  votes?: Record<string, 'for' | 'against' | 'abstain'>;
}

export interface PatchMeetingAgendaRequest {
  trustId: string;
  meetingId: string;
  items: PatchMeetingAgendaItem[];
}

export interface PatchMeetingAgendaResponse {
  meeting: TrustMeetingView;
  correlationId: string;
}

export function usePatchMeetingAgenda() {
  const qc = useQueryClient();
  return useMutation<PatchMeetingAgendaResponse, ApiError, PatchMeetingAgendaRequest>({
    mutationFn: ({ trustId, meetingId, items }) =>
      api<PatchMeetingAgendaResponse>(
        `/api/v1/app/trusts/${encodeURIComponent(trustId)}/meetings/${encodeURIComponent(meetingId)}/agenda`,
        { method: 'PATCH', body: { items } },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['trusts', 'meetings', vars.trustId],
      });
    },
  });
}

// ── end lane B ───────────────────────────────────────────────────────────────
